// src/screens/ResponsesScreen.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, ScrollView, Modal, TouchableOpacity, Alert, AppState } from 'react-native';
import {
  Appbar,
  Card,
  Title,
  Paragraph,
  Button,
  Text,
  FAB,
  ActivityIndicator,
  Badge,
  Divider,
  Chip,
  IconButton,
  ProgressBar,
} from 'react-native-paper';
import { AVAQualisTheme } from '../theme/theme';
import { styles } from './ResponsesScreen.css';
import { getFormData } from '../data/FormsData';
import * as api from '../lib/api';
import { transformResponseToApi } from '../utils/transformers';
import StorageManager from '../utils/StorageManager';
import AsyncStorage from '@react-native-async-storage/async-storage';
import AuthService from '../services/authService';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { getLevelEmoji, getLevelDescription } from '../data/EstruturaOrganizacional';

// Função de debounce
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const determineResponseSyncStatus = (localResponse, cloudResponse) => {
  // Se não há versão na nuvem, é somente local
  if (!cloudResponse) {
    return 'local-only';
  }

  // CORREÇÃO: Verificar se está realmente sincronizado
  if (localResponse.synced && localResponse.syncedAt && cloudResponse) {
    // Comparar se o conteúdo é igual
    const localAnswers = JSON.stringify(localResponse.answers || {});
    const cloudAnswers = JSON.stringify(cloudResponse.answers || {});

    if (localAnswers === cloudAnswers) {
      return 'synced';
    }

    // Se tem syncedAt mas conteúdo diferente, verificar datas
    const localDate = new Date(localResponse.lastModified || localResponse.syncedAt);
    const cloudDate = new Date(cloudResponse.lastModified || cloudResponse.syncedAt);

    // Margem de 10 segundos para considerar sincronizado
    if (Math.abs(localDate - cloudDate) < 10000) {
      return 'synced';
    }
  }

  // NOVA VERIFICAÇÃO: Se a resposta local tem flag synced=true e syncedAt recente
  if (localResponse.synced && localResponse.syncedAt) {
    const syncDate = new Date(localResponse.syncedAt);
    const cloudDate = new Date(cloudResponse.lastModified || cloudResponse.createdAt);

    // Se foi sincronizado após a última modificação da nuvem
    if (syncDate >= cloudDate) {
      return 'synced';
    }
  }

  // Comparar datas para determinar qual é mais recente
  const localModified = new Date(localResponse.lastModified || localResponse.createdAt);
  const cloudModified = new Date(cloudResponse.lastModified || cloudResponse.createdAt);

  if (localModified > cloudModified) {
    return 'local-newer';
  } else if (cloudModified > localModified) {
    return 'cloud-newer';
  }

  // Se as datas são iguais mas não está marcado como synced
  return 'synced'; // MUDANÇA: Considerar sincronizado se datas são iguais
};

// Função para extrair o sufixo de um ID completo
const extractFormIdSuffix = fullId => {
  if (!fullId) return 'unknown';

  // Verificar se é um ID no formato "custom-form-timestamp-suffix"
  const customFormMatch = fullId.match(/custom-form-\d+-([a-z0-9]+)$/);
  if (customFormMatch && customFormMatch[1]) {
    return customFormMatch[1]; // Retorna apenas o sufixo
  }

  // Se não for no formato esperado, retorna os últimos 8 caracteres
  return fullId.substr(-8);
};

// Componente Dialog Personalizado
const CustomDialog = ({ visible, onDismiss, title, message, actions }) => {
  return (
    <Modal visible={visible} transparent={true} animationType="fade" onRequestClose={onDismiss}>
      <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={onDismiss}>
        <TouchableOpacity style={styles.dialogContainer} activeOpacity={1} onPress={() => {}}>
          <View style={styles.dialogHeader}>
            <Text style={styles.dialogTitle}>{title}</Text>
          </View>

          <View style={styles.dialogContent}>
            <Text style={styles.dialogMessage}>{message}</Text>
          </View>

          <View style={styles.dialogActions}>
            {actions.map((action, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.dialogButton,
                  index === actions.length - 1
                    ? styles.dialogButtonPrimary
                    : styles.dialogButtonSecondary,
                ]}
                onPress={() => {
                  action.action();
                  onDismiss();
                }}
              >
                <Text
                  style={[
                    styles.dialogButtonText,
                    index === actions.length - 1
                      ? styles.dialogButtonTextPrimary
                      : styles.dialogButtonTextSecondary,
                  ]}
                >
                  {action.text}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

// Função auxiliar para garantir que apenas respostas do avaliador atual sejam mostradas
const filterResponsesByCurrentAvaliador = (responses, currentAvaliador) => {
  if (!currentAvaliador || !currentAvaliador.id) {
    console.log('⚠️ Nenhum avaliador logado para filtrar respostas');
    return [];
  }

  const filtered = responses.filter(response => {
    // Verificar se a resposta tem avaliador
    if (!response.avaliador || !response.avaliador.id) {
      console.log(`⚠️ Resposta ${response.id} sem avaliador definido, ignorando`);
      return false;
    }

    // Comparar IDs
    const belongsToCurrentUser = response.avaliador.id === currentAvaliador.id;

    if (!belongsToCurrentUser) {
      console.log(
        `🔒 Resposta ${response.id} pertence ao avaliador ${response.avaliador.nome} (${response.avaliador.id}), ignorando`
      );
    }

    return belongsToCurrentUser;
  });

  console.log(
    `👤 Filtradas ${filtered.length}/${responses.length} respostas para o avaliador ${currentAvaliador.nome} (${currentAvaliador.id})`
  );
  return filtered;
};

// Componente para exibir um badge de status
const StatusBadge = ({ syncStatus }) => {
  let backgroundColor, textColor, label, icon;

  switch (syncStatus) {
    case 'synced':
      backgroundColor = '#E8F5E9';
      textColor = '#2E7D32';
      label = 'Sincronizado';
      icon = '✅';
      break;
    case 'cloud-newer':
      backgroundColor = '#FFEBEE';
      textColor = '#D32F2F';
      label = 'Versão desatualizada';
      icon = '⚠️';
      break;
    case 'local-newer':
      backgroundColor = '#FFF8E1';
      textColor = '#FF6F00';
      label = 'Upload pendente';
      icon = '📤';
      break;
    case 'local-only':
      backgroundColor = '#FFECB3';
      textColor = '#FF8F00';
      label = 'Somente local';
      icon = '💾';
      break;
    default:
      backgroundColor = '#E0E0E0';
      textColor = '#757575';
      label = 'Status desconhecido';
      icon = '❓';
  }

  return (
    <View style={[styles.statusBadge, { backgroundColor }]}>
      <Text style={[styles.statusBadgeText, { color: textColor }]}>
        {icon} {label}
      </Text>
    </View>
  );
};

// Indicador de progresso melhorado
const FormProgressIndicator = ({ progress, answeredCount, totalQuestions, syncStatus }) => {
  // Determinar cores com base no status
  let progressColor, backgroundColor, textColor;

  switch (syncStatus) {
    case 'synced':
      progressColor = '#2E7D32'; // Verde para sincronizado
      backgroundColor = '#E8F5E9';
      textColor = '#2E7D32';
      break;
    case 'cloud-newer':
      progressColor = '#D32F2F'; // Vermelho para nuvem mais nova
      backgroundColor = '#FFEBEE';
      textColor = '#D32F2F';
      break;
    case 'local-newer':
      progressColor = '#FF6F00'; // Laranja para local mais novo
      backgroundColor = '#FFF8E1';
      textColor = '#FF6F00';
      break;
    case 'local-only':
      progressColor = '#FF8F00'; // Amarelo para somente local
      backgroundColor = '#FFECB3';
      textColor = '#FF8F00';
      break;
    default:
      progressColor = AVAQualisTheme.colors.primary;
      backgroundColor = `${AVAQualisTheme.colors.primary}20`;
      textColor = AVAQualisTheme.colors.primary;
  }

  return (
    <View style={[styles.progressIndicatorContainer, { backgroundColor }]}>
      <View style={styles.progressHeader}>
        <Text style={[styles.progressPercentage, { color: textColor }]}>
          <Text style={styles.progressPercentageBold}>{Math.round(progress * 100)}%</Text> concluído
        </Text>

        <Text style={[styles.progressFraction, { color: textColor }]}>
          <Text style={styles.progressBoldNumber}>{answeredCount}</Text>/
          {<Text style={styles.progressBoldNumber}>{totalQuestions}</Text>} perguntas respondidas
        </Text>
      </View>

      <View style={styles.progressBarContainer}>
        <View style={[styles.progressBarTrack, { backgroundColor: `${progressColor}30` }]}>
          <View
            style={[
              styles.progressBarFill,
              {
                backgroundColor: progressColor,
                width: `${Math.round(progress * 100)}%`,
              },
            ]}
          />
        </View>
      </View>

      {syncStatus === 'synced' && (
        <Text style={styles.progressStatusText}>
          ✅ Este formulário está sincronizado com a nuvem. Tudo correto e atualizado.
        </Text>
      )}

      {syncStatus === 'cloud-newer' && (
        <Text style={styles.progressStatusText}>
          ⚠️ Existe uma versão mais recente deste formulário na nuvem! Recomenda-se baixar esta
          versão antes de continuar.
        </Text>
      )}

      {syncStatus === 'local-newer' && (
        <Text style={styles.progressStatusText}>
          ⚠️ Sua versão local está mais recente que a versão da nuvem! Faça upload dessa versão para
          evitar perder informações.
        </Text>
      )}

      {syncStatus === 'local-only' && (
        <Text style={styles.progressStatusText}>
          ⚠️ Este formulário está salvo somente no seu dispositivo. Faça upload imediatamente para
          evitar perda de informações.
        </Text>
      )}
    </View>
  );
};

// Componente para exibir uma resposta de formulário
const ResponseItem = ({
  response,
  formatDate,
  summary,
  onSelect,
  onDelete,
  onUpload,
  onDownloadCloud,
  disabled,
  uploading,
  serverStatus,
  onViewCloudVersion,
  formTotalQuestions,
  responseValidations,
}) => {
  // Obter o total de perguntas
  const totalQuestions = response.totalQuestions || formTotalQuestions[response.formId] || 0;

  // Calcular a porcentagem de progresso
  const calculateProgress = () => {
    if (!totalQuestions || totalQuestions === 0) {
      const formTotal = formTotalQuestions[response.formId];
      if (!formTotal || formTotal === 0) return 0;

      if (response.syncStatus === 'cloud-newer' && response.cloudVersion) {
        const cloudAnsweredCount = Object.keys(response.cloudVersion.answers || {}).length;
        return cloudAnsweredCount / formTotal;
      } else {
        const answeredCount = Object.keys(response.answers || {}).length;
        return answeredCount / formTotal;
      }
    }

    if (response.syncStatus === 'cloud-newer' && response.cloudVersion) {
      const cloudAnsweredCount = Object.keys(response.cloudVersion.answers || {}).length;
      return cloudAnsweredCount / totalQuestions;
    } else {
      const answeredCount = Object.keys(response.answers || {}).length;
      return answeredCount / totalQuestions;
    }
  };

  const progress = calculateProgress();

  // Calcular o número correto de respostas
  const getAnsweredCount = () => {
    if (response.syncStatus === 'cloud-newer' && response.cloudVersion) {
      return Object.keys(response.cloudVersion.answers || {}).length;
    } else {
      return Object.keys(response.answers || {}).length;
    }
  };

  const answeredCount = getAnsweredCount();

  return (
    <Card
      style={[
        styles.responseCard,
        responseValidations[response.id] && styles.validatedCard, // Adicionar esta linha
        response.syncStatus === 'synced'
          ? styles.syncedCard
          : response.syncStatus === 'cloud-newer'
            ? styles.cloudNewerCard
            : response.syncStatus === 'local-newer'
              ? styles.localNewerCard
              : styles.localOnlyCard,
      ]}
    >
      <Card.Content>
        <View style={styles.responseHeader}>
          <View style={styles.titleSection}>
            <Title
              style={[
                styles.responseTitle,
                response.syncStatus === 'synced'
                  ? styles.syncedTitle
                  : response.syncStatus === 'cloud-newer'
                    ? styles.cloudNewerTitle
                    : response.syncStatus === 'local-newer'
                      ? styles.localNewerTitle
                      : styles.localOnlyTitle,
              ]}
            >
              {response.formTitle}
            </Title>

            <Text style={styles.responseDate}>
              📅 Respondido em: {formatDate(response.lastModified || response.createdAt)}
            </Text>

            {response.syncedAt && (
              <Text style={styles.syncDate}>
                ☁️ Última sincronização: {formatDate(response.syncedAt)}
              </Text>
            )}

            {response.syncStatus === 'cloud-newer' && response.cloudVersion && (
              <Text style={styles.cloudNewerDate}>
                🔄 Versão da nuvem é de: {formatDate(response.cloudVersion.lastModified)}
              </Text>
            )}

            <Text style={styles.responseId}>ID: {extractFormIdSuffix(response.formId)}</Text>
          </View>

          <StatusBadge syncStatus={response.syncStatus} />
        </View>

        {/* Barra de progresso melhorada */}
        <FormProgressIndicator
          progress={progress}
          answeredCount={answeredCount}
          totalQuestions={totalQuestions || '?'}
          syncStatus={response.syncStatus}
        />

        {/* Mensagem de servidor offline */}
        {serverStatus !== 'online' && response.syncStatus !== 'synced' && (
          <View style={styles.offlineWarningContainer}>
            <Text style={styles.offlineWarning}>
              ⚠️ Servidor offline. Não é possível enviar formulários no momento.
            </Text>
          </View>
        )}

        {/* Resumo das respostas */}
        {Object.keys(response.answers || {}).length > 0 && (
          <View style={styles.summaryContainer}>
            <TouchableOpacity
              style={styles.summaryHeader}
              onPress={() => {
                // Expandir/colapsar o resumo
              }}
            >
              <Text style={styles.summaryHeaderText}>📊 Resumo das Respostas</Text>
              <Text style={styles.summaryExpandIcon}>▼</Text>
            </TouchableOpacity>
            <View style={styles.summaryContent}>
              <Text style={styles.summaryText}>{summary}</Text>
            </View>
          </View>
        )}

        {/* Anexos */}
        {response.attachments && Object.keys(response.attachments).length > 0 && (
          <View style={styles.attachmentsPreview}>
            <Text style={styles.attachmentsPreviewTitle}>📎 Anexos:</Text>
            <Text style={styles.attachmentsPreviewText}>
              {Object.values(response.attachments).reduce((total, arr) => total + arr.length, 0)}{' '}
              anexo(s) em {Object.keys(response.attachments).length} pergunta(s)
            </Text>
          </View>
        )}

        {/* Badge de Validação ou Aviso de Pendente */}
        {/* Badge de Validação ou Aviso de Pendente */}
        {responseValidations[response.id] ? (
          // Badge de Validação
          <View style={styles.validationBadgeContainer}>
            <View style={styles.validationBadge}>
              <MaterialCommunityIcons name="check-decagram" size={20} color="white" />
              <Text style={styles.validationBadgeText}>Envio Validado</Text>
            </View>
            <View style={styles.validationDetails}>
              <MaterialCommunityIcons name="account-check" size={16} color="#4CAF50" />
              <View style={styles.validationInfo}>
                <Text style={styles.validationInfoText}>
                  Validado em{' '}
                  {new Date(responseValidations[response.id].dataValidacao).toLocaleDateString(
                    'pt-BR',
                    {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    }
                  )}
                </Text>
                <Text style={styles.validationByText}>
                  Por: {responseValidations[response.id].validadoPor}
                </Text>
              </View>
            </View>
            {/* Mensagem adicional sobre alterações */}
            <View style={styles.validationNoticeContainer}>
              <MaterialCommunityIcons name="lock" size={16} color="#616161" />
              <Text style={styles.validationNoticeText}>
                Este formulário não pode ser alterado após validação.
              </Text>
            </View>
            <View style={styles.validationContactContainer}>
              <MaterialCommunityIcons name="information" size={16} color="#1976D2" />
              <Text style={styles.validationContactText}>
                Para qualquer informação necessária, entre em contato com a coordenação do
                AVAQualis.
              </Text>
            </View>
          </View>
        ) : (
          // Aviso de Pendente de Validação
          response.syncStatus === 'synced' && (
            <View style={styles.pendingValidationContainer}>
              <MaterialCommunityIcons name="alert-circle" size={20} color="#FF8F00" />
              <View style={styles.pendingValidationInfo}>
                <Text style={styles.pendingValidationTitle}>Pendente de Validação</Text>
                <Text style={styles.pendingValidationText}>
                  Este formulário pode ser editado ou excluído até ser validado pela coordenação.
                </Text>
              </View>
            </View>
          )
        )}
      </Card.Content>

      <Card.Actions style={styles.cardActions}>
        {/* Só mostrar botões se NÃO estiver validado */}
        {!responseValidations[response.id] && (
          <>
            <Button
              mode="outlined"
              onPress={() => onDelete(response.id, response.formTitle)}
              icon="delete-outline"
              compact
              textColor={
                response.syncStatus === 'synced'
                  ? AVAQualisTheme.colors.secondary
                  : AVAQualisTheme.colors.error
              }
              style={
                response.syncStatus === 'synced' ? styles.deleteButtonSynced : styles.deleteButton
              }
            >
              {response.syncStatus === 'synced' ? '🗑️ Remover Local' : '🗑️ Excluir'}
            </Button>

            <Button
              mode="outlined"
              onPress={() => onSelect({ id: response.formId, title: response.formTitle })}
              icon="pencil"
              compact
              textColor={AVAQualisTheme.colors.primary}
              style={styles.editButton}
            >
              {response.syncStatus === 'synced' ? '📝 Editar Cópia' : '📝 Continuar'}
            </Button>
          </>
        )}
        {/* Botão para baixar a versão da nuvem */}
        {response.syncStatus === 'cloud-newer' && (
          <Button
            mode="contained"
            onPress={() => onDownloadCloud(response)}
            icon="cloud-download"
            compact
            buttonColor="#D32F2F"
            style={styles.downloadButton}
            disabled={disabled || serverStatus !== 'online'}
          >
            📥 Baixar da Nuvem
          </Button>
        )}

        {/* Botão de upload */}
        {(response.syncStatus === 'local-newer' || response.syncStatus === 'local-only') &&
          !responseValidations[response.id] && (
            <Button
              mode="contained"
              onPress={() => onUpload(response.id)}
              loading={uploading}
              icon="upload"
              compact
              buttonColor={response.syncStatus === 'local-newer' ? '#FF6F00' : '#FF8F00'}
              style={[
                styles.uploadButton,
                response.syncStatus === 'local-newer'
                  ? styles.localNewerUploadButton
                  : styles.localOnlyUploadButton,
              ]}
              disabled={disabled || serverStatus !== 'online'}
            >
              {response.syncStatus === 'local-newer'
                ? '📤 Atualizar Nuvem'
                : '📤 Enviar para Nuvem'}
            </Button>
          )}

        {/* Botão para ver versão na nuvem */}
        {(response.syncStatus === 'local-newer' || response.syncStatus === 'cloud-newer') && (
          <Button
            mode="outlined"
            onPress={() => onViewCloudVersion(response)}
            icon="compare"
            compact
            textColor="#1976D2"
            style={styles.viewCloudButton}
            disabled={serverStatus !== 'online'}
          >
            🔄 Comparar Versões
          </Button>
        )}
      </Card.Actions>
    </Card>
  );
};

// Visualizador de conflitos entre versões
const ConflictViewer = ({
  visible,
  onDismiss,
  localVersion,
  cloudVersion,
  onUseLocal,
  onUseCloud,
}) => {
  if (!visible || !localVersion || !cloudVersion) return null;

  // Calcular diferenças
  const getDifferences = () => {
    const differences = [];

    const allQuestionIds = [
      ...new Set([
        ...Object.keys(localVersion.answers || {}),
        ...Object.keys(cloudVersion.answers || {}),
      ]),
    ];

    allQuestionIds.forEach(questionId => {
      const localAnswer = localVersion.answers?.[questionId];
      const cloudAnswer = cloudVersion.answers?.[questionId];

      if (localAnswer !== cloudAnswer) {
        differences.push({
          questionId,
          local: localAnswer || 'Não respondido',
          cloud: cloudAnswer || 'Não respondido',
        });
      }
    });

    return differences;
  };

  const differences = getDifferences();

  // Determinar qual versão é mais recente
  const localDate = new Date(localVersion.lastModified || localVersion.createdAt);
  const cloudDate = new Date(
    cloudVersion.lastModified || cloudVersion.syncedAt || cloudVersion.createdAt
  );
  const isLocalNewer = localDate > cloudDate;

  // Calcular a contagem de respostas
  const localAnswerCount = Object.keys(localVersion.answers || {}).length;
  const cloudAnswerCount = Object.keys(cloudVersion.answers || {}).length;

  // Calcular progresso
  const totalQuestions = localVersion.totalQuestions || cloudVersion.totalQuestions || 0;
  const localProgress =
    totalQuestions > 0 ? Math.round((localAnswerCount / totalQuestions) * 100) : 0;
  const cloudProgress =
    totalQuestions > 0 ? Math.round((cloudAnswerCount / totalQuestions) * 100) : 0;

  return (
    <Modal visible={visible} transparent={true} animationType="slide" onRequestClose={onDismiss}>
      <View style={styles.conflictModalOverlay}>
        <View style={styles.conflictModalContainer}>
          <View style={styles.conflictModalHeader}>
            <Text style={styles.conflictModalTitle}>🔄 Comparação de Versões</Text>
            <IconButton
              icon="close"
              size={24}
              onPress={onDismiss}
              iconColor={AVAQualisTheme.colors.error}
            />
          </View>

          <ScrollView style={styles.conflictModalContent}>
            <Text style={styles.conflictModalDescription}>
              {isLocalNewer
                ? 'Sua versão local é mais recente que a versão na nuvem.'
                : 'Existe uma versão mais recente disponível na nuvem.'}{' '}
              Escolha qual versão deseja manter.
            </Text>

            <View style={styles.conflictDatesContainer}>
              <View
                style={[
                  styles.conflictDateItem,
                  isLocalNewer && styles.conflictDateItemHighlighted,
                ]}
              >
                <Text style={styles.conflictDateLabel}>Versão Local:</Text>
                <Text style={styles.conflictDateValue}>
                  Modificada em{' '}
                  {new Date(localVersion.lastModified || localVersion.createdAt).toLocaleString()}
                  {isLocalNewer && ' (Mais recente)'}
                </Text>
                <Text style={styles.conflictProgressValue}>
                  Progresso: {localProgress}% ({localAnswerCount} respostas)
                </Text>
              </View>
              <View
                style={[
                  styles.conflictDateItem,
                  !isLocalNewer && styles.conflictDateItemHighlighted,
                ]}
              >
                <Text style={styles.conflictDateLabel}>Versão na Nuvem:</Text>
                <Text style={styles.conflictDateValue}>
                  {cloudVersion.syncedAt
                    ? `Sincronizada em ${new Date(cloudVersion.syncedAt).toLocaleString()}`
                    : `Modificada em ${new Date(cloudVersion.lastModified || cloudVersion.createdAt).toLocaleString()}`}
                  {!isLocalNewer && ' (Mais recente)'}
                </Text>
                <Text style={styles.conflictProgressValue}>
                  Progresso: {cloudProgress}% ({cloudAnswerCount} respostas)
                </Text>
              </View>
            </View>

            <Divider style={styles.conflictDivider} />

            <Text style={styles.conflictSectionTitle}>
              Diferenças Encontradas ({differences.length})
            </Text>

            {differences.length > 0 ? (
              differences.map((diff, index) => (
                <View key={diff.questionId} style={styles.conflictItem}>
                  <Text style={styles.conflictItemTitle}>
                    Pergunta {index + 1}: {diff.questionId}
                  </Text>
                  <View style={styles.conflictItemContent}>
                    <View
                      style={[
                        styles.conflictItemLocal,
                        isLocalNewer && styles.conflictItemHighlighted,
                      ]}
                    >
                      <Text style={styles.conflictItemLabel}>Versão Local:</Text>
                      <Text style={styles.conflictItemValue}>{diff.local}</Text>
                    </View>
                    <View
                      style={[
                        styles.conflictItemCloud,
                        !isLocalNewer && styles.conflictItemHighlighted,
                      ]}
                    >
                      <Text style={styles.conflictItemLabel}>Versão na Nuvem:</Text>
                      <Text style={styles.conflictItemValue}>{diff.cloud}</Text>
                    </View>
                  </View>
                </View>
              ))
            ) : (
              <Text style={styles.noConflictsText}>
                Não foram encontradas diferenças nas respostas entre as versões.
                {isLocalNewer
                  ? ' A versão local é apenas mais recente.'
                  : ' A versão da nuvem é apenas mais recente.'}
              </Text>
            )}
          </ScrollView>

          <View style={styles.conflictModalActions}>
            <Button
              mode={!isLocalNewer ? 'contained' : 'outlined'}
              onPress={onUseCloud}
              icon="cloud-download"
              style={!isLocalNewer ? styles.conflictRecommendedButton : styles.conflictCloudButton}
            >
              ☁️ {!isLocalNewer ? 'Usar Versão da Nuvem (Recomendado)' : 'Usar Versão da Nuvem'}
            </Button>
            <Button
              mode={isLocalNewer ? 'contained' : 'outlined'}
              onPress={onUseLocal}
              icon="content-save"
              style={isLocalNewer ? styles.conflictRecommendedButton : styles.conflictLocalButton}
            >
              💾 {isLocalNewer ? 'Usar Minha Versão (Recomendado)' : 'Usar Minha Versão'}
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default function ResponsesScreen({ onBack, onSelectForm }) {
  // Estados principais
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [currentUploadId, setCurrentUploadId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogContent, setDialogContent] = useState({
    title: '',
    message: '',
    actions: [],
  });
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [serverStatus, setServerStatus] = useState(null);

  // Estados para monitorar AppState
  const [appState, setAppState] = useState(AppState.currentState);
  const [lastFocusTime, setLastFocusTime] = useState(Date.now());

  // Estados para conflitos
  const [conflictModalVisible, setConflictModalVisible] = useState(false);
  const [localVersion, setLocalVersion] = useState(null);
  const [cloudVersion, setCloudVersion] = useState(null);

  // Estado para armazenar os resumos das respostas
  const [responseSummaries, setResponseSummaries] = useState({});

  // Estado para filtros
  const [activeFilter, setActiveFilter] = useState('all');

  // Estado para armazenar o número total de perguntas por formulário
  const [formTotalQuestions, setFormTotalQuestions] = useState({});

  // Estado para armazenar validações das respostas
  const [responseValidations, setResponseValidations] = useState({});

  // Buscar validações de uma resposta específica
  const getResponseValidation = useCallback(
    async responseId => {
      try {
        console.log('🔍 Buscando validação para resposta:', responseId);

        let validation = null;

        // PRIMEIRO: Tentar buscar do servidor se estiver online
        if (serverStatus === 'online') {
          try {
            console.log('🌐 Buscando validação do servidor...');

            // Buscar resposta específica do servidor
            const serverResponse = await api.getResponseById(responseId);

            if (
              serverResponse &&
              serverResponse.validacao &&
              serverResponse.validacao.validadoPor
            ) {
              validation = {
                validadoPor: serverResponse.validacao.validadoPor.nome,
                dataValidacao: serverResponse.validacao.dataValidacao,
              };

              console.log('✅ Validação encontrada no servidor:', validation);

              // IMPORTANTE: Salvar a resposta validada localmente para cache
              try {
                await StorageManager.saveResponse({
                  ...serverResponse,
                  synced: true,
                  syncedAt: serverResponse.syncedAt || new Date().toISOString(),
                });
                console.log('💾 Resposta validada salva localmente');
              } catch (saveError) {
                console.warn('⚠️ Erro ao salvar resposta validada localmente:', saveError);
              }

              return validation;
            }
          } catch (serverError) {
            console.warn('⚠️ Erro ao buscar validação do servidor:', serverError);
          }
        }

        // SEGUNDO: Se não encontrou no servidor ou está offline, buscar localmente
        console.log('📱 Buscando validação localmente...');
        const allResponses = await StorageManager.loadFormResponses();
        const response = allResponses.find(r => r.id === responseId);

        if (response && response.validacao && response.validacao.validadoPor) {
          validation = {
            validadoPor: response.validacao.validadoPor.nome,
            dataValidacao: response.validacao.dataValidacao,
          };
          console.log('✅ Validação encontrada localmente:', validation);
        }

        return validation;
      } catch (error) {
        console.error('❌ Erro ao buscar validação:', error);
        return null;
      }
    },
    [serverStatus]
  );

  // Carregar validações para todas as respostas
  const loadAllValidations = useCallback(
    async responses => {
      try {
        console.log('🔄 Carregando validações para todas as respostas...');
        const validations = {};

        // Se o servidor estiver online, buscar validações atualizadas
        if (serverStatus === 'online') {
          try {
            console.log('🌐 Buscando validações do servidor...');

            // Buscar TODAS as respostas do servidor para o avaliador atual
            const currentAvaliador = AuthService.getAvaliador();
            if (currentAvaliador && currentAvaliador.id) {
              const serverResponses = await api.getResponses();

              // Filtrar apenas respostas do avaliador atual que estão validadas
              const validatedResponses = serverResponses.filter(
                r =>
                  r.avaliador &&
                  r.avaliador.id === currentAvaliador.id &&
                  r.validacao &&
                  r.validacao.validadoPor
              );

              console.log(
                `✅ ${validatedResponses.length} respostas validadas encontradas no servidor`
              );

              // Processar cada resposta validada
              for (const serverResponse of validatedResponses) {
                validations[serverResponse.id] = {
                  validadoPor: serverResponse.validacao.validadoPor.nome,
                  dataValidacao: serverResponse.validacao.dataValidacao,
                };

                // Salvar localmente para cache
                try {
                  await StorageManager.saveResponse({
                    ...serverResponse,
                    synced: true,
                    syncedAt: serverResponse.syncedAt || new Date().toISOString(),
                  });
                } catch (saveError) {
                  console.warn('⚠️ Erro ao salvar resposta validada:', saveError);
                }
              }
            }
          } catch (serverError) {
            console.error('❌ Erro ao buscar validações do servidor:', serverError);
          }
        }

        // Também verificar validações locais (para quando estiver offline)
        for (const response of responses) {
          if (!validations[response.id]) {
            const validation = await getResponseValidation(response.id);
            if (validation) {
              validations[response.id] = validation;
            }
          }
        }

        setResponseValidations(validations);
        console.log('✅ Validações carregadas:', validations);
      } catch (error) {
        console.error('❌ Erro ao carregar validações:', error);
      }
    },
    [serverStatus, getResponseValidation]
  );

  // Estado para armazenar o avaliador logado
  const [currentAvaliador, setCurrentAvaliador] = useState(null);

  // Carregar avaliador logado
  useEffect(() => {
    const loadCurrentAvaliador = () => {
      const avaliador = AuthService.getAvaliador();
      console.log('👤 Avaliador logado:', avaliador);
      setCurrentAvaliador(avaliador);
    };

    loadCurrentAvaliador();
  }, []);

  // Função para carregar respostas do StorageManager e compará-las com as da nuvem - VERSÃO CORRIGIDA COM MELHOR AGREGAÇÃO
  // FUNÇÃO CORRIGIDA: Carregar respostas APENAS de formulários que existem
  // Cache de formulários válidos (evita múltiplas verificações)
  const [validFormIds, setValidFormIds] = useState(new Set());
  const [invalidFormIds, setInvalidFormIds] = useState(new Set());
  const [lastValidationTime, setLastValidationTime] = useState(0);
  const VALIDATION_CACHE_TIME = 5 * 60 * 1000; // 5 minutos

  // NOVA FUNÇÃO: Verificar se um formulário ainda existe
  const checkFormExists = async formId => {
    try {
      // Se já está no cache de válidos, retornar true imediatamente
      if (validFormIds.has(formId)) {
        return true;
      }

      // Se já está no cache de inválidos, retornar false imediatamente
      if (invalidFormIds.has(formId)) {
        return false;
      }

      // Tentar buscar localmente primeiro (para modo offline)
      const forms = await StorageManager.loadCustomForms();
      const existsLocally = forms.some(f => f.id === formId);

      if (existsLocally) {
        setValidFormIds(prev => new Set([...prev, formId]));
        return true;
      }

      // Se não existe localmente e estamos offline, considerar inválido
      if (serverStatus !== 'online') {
        console.log(`⚠️ Formulário ${formId} não existe localmente e servidor está offline`);
        setInvalidFormIds(prev => new Set([...prev, formId]));
        return false;
      }

      // Se estamos online, verificar no servidor
      try {
        await api.getFormById(formId);
        setValidFormIds(prev => new Set([...prev, formId]));
        return true;
      } catch (error) {
        console.warn(`⚠️ Formulário ${formId} não existe no servidor:`, error);
        setInvalidFormIds(prev => new Set([...prev, formId]));
        return false;
      }
    } catch (error) {
      console.error(`❌ Erro ao verificar formulário ${formId}:`, error);
      // Em caso de erro, assumir que existe para não perder dados
      return true;
    }
  };

  // NOVA FUNÇÃO: Validar e limpar respostas de formulários inexistentes
  const validateAndCleanResponses = async responses => {
    try {
      // Verificar se precisa validar (não fazer sempre para não travar)
      const now = Date.now();
      const shouldValidate = now - lastValidationTime > VALIDATION_CACHE_TIME;

      if (!shouldValidate && serverStatus === 'online') {
        // Ainda não é hora de validar, retornar respostas normalmente
        return responses;
      }

      console.log('🔍 Validando formulários das respostas...');

      const validResponses = [];
      const invalidResponseIds = [];

      for (const response of responses) {
        const exists = await checkFormExists(response.formId);

        if (exists) {
          validResponses.push(response);
        } else {
          console.warn(`🗑️ Resposta ${response.id} é de formulário inexistente ${response.formId}`);
          invalidResponseIds.push(response.id);
        }
      }

      // Remover respostas inválidas do storage
      if (invalidResponseIds.length > 0) {
        console.log(
          `🗑️ Removendo ${invalidResponseIds.length} respostas de formulários inexistentes`
        );

        for (const responseId of invalidResponseIds) {
          try {
            await StorageManager.removeResponse(responseId);
          } catch (removeError) {
            console.error(`❌ Erro ao remover resposta ${responseId}:`, removeError);
          }
        }

        // Mostrar notificação para o usuário
        showDialog(
          '🗑️ Limpeza Automática',
          `${invalidResponseIds.length} resposta(s) de formulários que não existem mais foram removidas automaticamente.`,
          [{ text: '✅ OK', action: () => setDialogVisible(false) }]
        );
      }

      // Atualizar timestamp da última validação
      setLastValidationTime(now);

      return validResponses;
    } catch (error) {
      console.error('❌ Erro ao validar respostas:', error);
      // Em caso de erro, retornar respostas originais
      return responses;
    }
  };

  // SUBSTITUIR A FUNÇÃO loadResponses EXISTENTE POR ESTA VERSÃO CORRIGIDA:
  const loadResponses = useCallback(async () => {
    try {
      if (!currentAvaliador) {
        console.log('⚠️ Nenhum avaliador logado, não é possível carregar respostas');
        setResponses([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      await StorageManager.initialize();

      // Carregar respostas locais
      const localResponses = await StorageManager.loadFormResponses();
      console.log(`📋 Carregadas ${localResponses.length} respostas locais`);

      // Filtrar apenas respostas do avaliador atual
      const filteredLocalResponses = filterResponsesByCurrentAvaliador(
        localResponses,
        currentAvaliador
      );

      // NOVA ETAPA: Validar e limpar respostas de formulários inexistentes
      const validResponses = await validateAndCleanResponses(filteredLocalResponses);

      console.log(`📋 Respostas válidas para avaliador ${currentAvaliador.nome}:`, {
        total: validResponses.length,
        removed: filteredLocalResponses.length - validResponses.length,
      });

      const totalsMap = await loadFormTotalQuestions();
      const isOnline = await checkServerConnection();

      if (isOnline) {
        try {
          const serverResponses = await api.getResponses();

          const filteredServerResponses = serverResponses.filter(response => {
            const belongsToCurrentUser =
              response.avaliador && response.avaliador.id === currentAvaliador.id;

            if (!belongsToCurrentUser && response.avaliador) {
              console.log(
                `🔒 Resposta do servidor ${response.id} pertence ao avaliador ${response.avaliador.nome} (${response.avaliador.id}), ignorando`
              );
            }

            return belongsToCurrentUser;
          });

          console.log(`☁️ Respostas do servidor para avaliador ${currentAvaliador.nome}:`, {
            total: filteredServerResponses.length,
          });

          // Agrupar e manter APENAS a versão mais recente
          const latestResponsesMap = new Map();

          // Processar respostas locais válidas - manter apenas a mais recente
          validResponses.forEach(localResp => {
            const formId = localResp.formId;
            const avaliadorId = localResp.avaliador?.id || 'unknown';
            const key = `${formId}_${avaliadorId}`;

            const existing = latestResponsesMap.get(key);

            if (
              !existing ||
              new Date(localResp.lastModified || localResp.createdAt) >
                new Date(existing.local?.lastModified || existing.local?.createdAt || 0)
            ) {
              latestResponsesMap.set(key, {
                local: localResp,
                cloud: existing?.cloud || null,
              });
            }
          });

          // Processar respostas do servidor
          filteredServerResponses.forEach(serverResp => {
            const formId = serverResp.formId;
            const avaliadorId = serverResp.avaliador?.id || 'unknown';
            const key = `${formId}_${avaliadorId}`;

            const existing = latestResponsesMap.get(key);

            if (existing) {
              existing.cloud = serverResp;
            } else {
              latestResponsesMap.set(key, {
                local: null,
                cloud: serverResp,
              });
            }
          });

          console.log(`🗂️ Grupos únicos criados: ${latestResponsesMap.size}`);

          // Processar cada grupo para determinar o status
          const processedResponses = [];
          console.log(`🔄 Processando ${latestResponsesMap.size} grupos de respostas...`);

          for (const [key, group] of latestResponsesMap) {
            // NOVA VERIFICAÇÃO: Confirmar que o formulário existe antes de processar
            const formId = key.split('_')[0];
            const formExists = await checkFormExists(formId);

            if (!formExists) {
              console.log(`⚠️ Ignorando resposta de formulário inexistente: ${formId}`);

              // Remover do servidor se estiver lá
              if (group.cloud) {
                try {
                  await api.deleteResponse(group.cloud.id);
                  console.log(`✅ Resposta ${group.cloud.id} removida do servidor`);
                } catch (deleteError) {
                  console.warn(`⚠️ Erro ao remover resposta do servidor:`, deleteError);
                }
              }

              // Remover localmente se estiver aqui
              if (group.local) {
                try {
                  await StorageManager.removeResponse(group.local.id);
                  console.log(`✅ Resposta ${group.local.id} removida localmente`);
                } catch (removeError) {
                  console.warn(`⚠️ Erro ao remover resposta local:`, removeError);
                }
              }

              continue; // Pular este grupo
            }

            // Processar normalmente se o formulário existe
            if (group.local && group.cloud) {
              // CORREÇÃO: Verificar se está realmente sincronizado
              const isSynced = group.local.synced && group.local.syncedAt;
              const syncDate = isSynced ? new Date(group.local.syncedAt) : null;
              const cloudDate = new Date(group.cloud.lastModified || group.cloud.createdAt);

              // Comparar conteúdo
              const localAnswers = JSON.stringify(group.local.answers || {});
              const cloudAnswers = JSON.stringify(group.cloud.answers || {});
              const contentMatch = localAnswers === cloudAnswers;

              // NOVA LÓGICA: Determinar status baseado em múltiplos fatores
              if (isSynced && syncDate >= cloudDate && contentMatch) {
                // Está sincronizado: flag synced=true E conteúdo igual E data de sync >= cloud
                processedResponses.push({
                  ...group.local,
                  synced: true,
                  syncedAt: group.local.syncedAt,
                  syncStatus: 'synced',
                  totalQuestions:
                    group.local.totalQuestions ||
                    group.cloud.totalQuestions ||
                    totalsMap[group.local.formId] ||
                    0,
                });
              } else if (
                contentMatch &&
                Math.abs(cloudDate - new Date(group.local.lastModified)) < 10000
              ) {
                // Conteúdo igual e datas muito próximas (menos de 10 segundos)
                processedResponses.push({
                  ...group.local,
                  synced: true,
                  syncedAt: new Date().toISOString(),
                  syncStatus: 'synced',
                  totalQuestions:
                    group.local.totalQuestions ||
                    group.cloud.totalQuestions ||
                    totalsMap[group.local.formId] ||
                    0,
                });
              } else {
                // Verificar qual é mais recente pelo conteúdo
                const localAnswersCount = Object.keys(group.local.answers || {}).length;
                const cloudAnswersCount = Object.keys(group.cloud.answers || {}).length;

                if (localAnswersCount > cloudAnswersCount) {
                  processedResponses.push({
                    ...group.local,
                    syncStatus: 'local-newer',
                    cloudVersion: group.cloud,
                    totalQuestions:
                      group.local.totalQuestions ||
                      group.cloud.totalQuestions ||
                      totalsMap[group.local.formId] ||
                      0,
                  });
                } else {
                  const localDate = new Date(group.local.lastModified || group.local.createdAt);

                  if (localDate > cloudDate) {
                    processedResponses.push({
                      ...group.local,
                      syncStatus: 'local-newer',
                      cloudVersion: group.cloud,
                      totalQuestions:
                        group.local.totalQuestions ||
                        group.cloud.totalQuestions ||
                        totalsMap[group.local.formId] ||
                        0,
                    });
                  } else {
                    processedResponses.push({
                      ...group.local,
                      syncStatus: 'cloud-newer',
                      cloudVersion: group.cloud,
                      totalQuestions:
                        group.local.totalQuestions ||
                        group.cloud.totalQuestions ||
                        totalsMap[group.local.formId] ||
                        0,
                    });
                  }
                }
              }
            } else if (group.local) {
              processedResponses.push({
                ...group.local,
                syncStatus: 'local-only',
                totalQuestions: group.local.totalQuestions || totalsMap[group.local.formId] || 0,
              });
            } else if (group.cloud) {
              const cloudResp = {
                ...group.cloud,
                synced: true,
                syncedAt: group.cloud.syncedAt || group.cloud.lastModified,
                syncStatus: 'synced',
                totalQuestions: group.cloud.totalQuestions || totalsMap[group.cloud.formId] || 0,
              };

              await StorageManager.saveResponse(cloudResp);
              processedResponses.push(cloudResp);
            }
          }

          console.log(`📊 Respostas processadas final:`, {
            total: processedResponses.length,
            synced: processedResponses.filter(r => r.syncStatus === 'synced').length,
            localNewer: processedResponses.filter(r => r.syncStatus === 'local-newer').length,
            cloudNewer: processedResponses.filter(r => r.syncStatus === 'cloud-newer').length,
            localOnly: processedResponses.filter(r => r.syncStatus === 'local-only').length,
          });

          // Log de debug para verificar status
          console.log(`📊 Status final das respostas:`, {
            synced: processedResponses.filter(r => r.syncStatus === 'synced').length,
            localNewer: processedResponses.filter(r => r.syncStatus === 'local-newer').length,
            cloudNewer: processedResponses.filter(r => r.syncStatus === 'cloud-newer').length,
            localOnly: processedResponses.filter(r => r.syncStatus === 'local-only').length,
          });

          const sortedResponses = sortResponses(processedResponses);
          setResponses(sortedResponses);
          await loadAllValidations(sortedResponses);
        } catch (error) {
          console.error('❌ Erro ao buscar respostas do servidor:', error);

          // Fallback: usar apenas respostas locais válidas
          const latestLocalMap = new Map();

          validResponses.forEach(resp => {
            const key = `${resp.formId}_${resp.avaliador?.id || 'unknown'}`;
            const existing = latestLocalMap.get(key);

            if (
              !existing ||
              new Date(resp.lastModified || resp.createdAt) >
                new Date(existing.lastModified || existing.createdAt)
            ) {
              latestLocalMap.set(key, resp);
            }
          });

          const uniqueResponses = Array.from(latestLocalMap.values()).map(resp => ({
            ...resp,
            syncStatus: resp.synced ? 'synced' : 'local-only',
            totalQuestions: resp.totalQuestions || totalsMap[resp.formId] || 0,
          }));

          const sortedResponses = sortResponses(uniqueResponses);
          setResponses(sortedResponses);
          await loadAllValidations(sortedResponses);
        }
      } else {
        console.log('📱 Servidor offline, usando apenas respostas locais válidas');

        const latestLocalMap = new Map();

        validResponses.forEach(resp => {
          const key = `${resp.formId}_${resp.avaliador?.id || 'unknown'}`;
          const existing = latestLocalMap.get(key);

          if (
            !existing ||
            new Date(resp.lastModified || resp.createdAt) >
              new Date(existing.lastModified || existing.createdAt)
          ) {
            latestLocalMap.set(key, resp);
          }
        });

        const uniqueResponses = Array.from(latestLocalMap.values()).map(resp => ({
          ...resp,
          syncStatus: resp.synced ? 'synced' : 'local-only',
          totalQuestions: resp.totalQuestions || totalsMap[resp.formId] || 0,
        }));

        const sortedResponses = sortResponses(uniqueResponses);
        setResponses(sortedResponses);
        await loadAllValidations(sortedResponses);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar respostas:', error);
      setResponses([]);
    } finally {
      setLoading(false);
    }
  }, [currentAvaliador, serverStatus]);

  // ADICIONAR ESTE useEffect PARA LIMPAR CACHE PERIODICAMENTE
  useEffect(() => {
    // Limpar cache de validação a cada 10 minutos
    const cacheCleanupInterval = setInterval(
      () => {
        setValidFormIds(new Set());
        setInvalidFormIds(new Set());
        setLastValidationTime(0);
        console.log('🧹 Cache de validação de formulários limpo');
      },
      10 * 60 * 1000
    );

    return () => clearInterval(cacheCleanupInterval);
  }, []);

  // Debounced loadResponses
  const loadResponsesDebounced = useMemo(() => debounce(loadResponses, 1000), [loadResponses]);

  // Carregar respostas e verificar conexão ao iniciar
  useEffect(() => {
    async function initialize() {
      await StorageManager.initialize();

      // Tentar carregar totais do AsyncStorage primeiro
      try {
        const savedTotals = await AsyncStorage.getItem('form_total_questions');
        if (savedTotals) {
          setFormTotalQuestions(JSON.parse(savedTotals));
        }
      } catch (e) {
        console.warn('⚠️ Erro ao carregar totais do AsyncStorage:', e);
      }

      loadResponses();
      checkServerConnection();
      loadFormTotalQuestions();
      // Forçar carregamento de validações após 2 segundos
      setTimeout(() => {
        if (mountedRef.current && responses.length > 0) {
          loadAllValidations(responses);
        }
      }, 2000);
    }

    // Só inicializar se tivermos um avaliador
    if (currentAvaliador) {
      initialize();
    }
  }, [currentAvaliador, loadResponses]);

  // Recarregar validações quando o servidor ficar online
  useEffect(() => {
    if (serverStatus === 'online' && responses.length > 0) {
      console.log('🔄 Servidor online, recarregando validações...');
      loadAllValidations(responses);
    }
  }, [serverStatus, responses.length]);

  // Monitorar mudanças no AppState
  useEffect(() => {
    const subscription = AppState.addEventListener('change', nextAppState => {
      if (appState.match(/inactive|background/) && nextAppState === 'active') {
        console.log('🔄 App voltou ao primeiro plano, recarregando respostas...');
        loadResponsesDebounced();
        // Recarregar validações também
        const currentResponses = responses;
        if (currentResponses.length > 0) {
          loadAllValidations(currentResponses);
        }
        setLastFocusTime(Date.now());
      }
      setAppState(nextAppState);
    });

    return () => {
      subscription.remove();
    };
  }, [appState, loadResponsesDebounced]);

  // Atualizações periódicas
  useEffect(() => {
    const loadInterval = setInterval(() => {
      if (!loading && !uploading) {
        loadResponsesDebounced();
      }
    }, 120000);

    return () => {
      clearInterval(loadInterval);
    };
  }, [loading, uploading, loadResponsesDebounced]);

  // Gerar resumos quando as respostas mudarem
  useEffect(() => {
    async function generateAllSummaries() {
      const summaries = {};
      for (const response of responses) {
        if (Object.keys(response.answers || {}).length > 0) {
          summaries[response.id] = await generateResponseSummary(response.answers, response.formId);
        } else {
          summaries[response.id] = '📝 Nenhuma resposta registrada';
        }
      }
      setResponseSummaries(summaries);
    }

    generateAllSummaries();
  }, [responses]);

  // Carregar o número total de perguntas para todos os formulários
  const loadFormTotalQuestions = async () => {
    try {
      console.log('🔄 Carregando totais de perguntas dos formulários...');

      // Obter todos os formulários locais
      const forms = await StorageManager.loadCustomForms();

      // Obter todas as respostas locais
      const responses = await StorageManager.loadFormResponses();

      // Criar mapa de ID do formulário para total de perguntas
      const totalsMap = {};

      // Processar formulários locais
      forms.forEach(form => {
        if (form.id) {
          const totalQuestions =
            form.totalQuestions ||
            (form.requirements ? form.requirements.length : 0) ||
            (form.questions ? form.questions.length : 0);

          if (totalQuestions > 0) {
            totalsMap[form.id] = totalQuestions;
          }
        }
      });

      // Processar totais de respostas locais
      responses.forEach(response => {
        if (
          response.formId &&
          response.totalQuestions &&
          (!totalsMap[response.formId] || totalsMap[response.formId] < response.totalQuestions)
        ) {
          totalsMap[response.formId] = response.totalQuestions;
        }
      });

      // Verificar se o servidor está online para buscar formulários que faltam
      const isOnline = await checkServerConnection();

      if (isOnline) {
        try {
          // Buscar formulários do servidor para obter totalQuestions
          const serverForms = await api.getForms();

          // Adicionar ao mapa
          serverForms.forEach(form => {
            if (form.id && !totalsMap[form.id]) {
              const totalQuestions =
                form.totalQuestions ||
                (form.requirements ? form.requirements.length : 0) ||
                (form.questions ? form.questions.length : 0);

              if (totalQuestions > 0) {
                totalsMap[form.id] = totalQuestions;
              }
            }
          });
        } catch (error) {
          console.error('❌ Erro ao buscar formulários do servidor:', error);
        }
      }

      console.log(
        `📊 Totais de perguntas carregados para ${Object.keys(totalsMap).length} formulários`
      );

      // Atualizar o estado
      setFormTotalQuestions(totalsMap);

      // Salvar totais no AsyncStorage para persistência
      try {
        await AsyncStorage.setItem('form_total_questions', JSON.stringify(totalsMap));
      } catch (storageError) {
        console.warn('⚠️ Erro ao salvar totais no AsyncStorage:', storageError);
      }

      return totalsMap;
    } catch (error) {
      console.error('❌ Erro ao carregar totais de perguntas:', error);

      // Tentar carregar totais do AsyncStorage como fallback
      try {
        const savedTotals = await AsyncStorage.getItem('form_total_questions');
        if (savedTotals) {
          const totalsMap = JSON.parse(savedTotals);
          setFormTotalQuestions(totalsMap);
          return totalsMap;
        }
      } catch (storageError) {
        console.error('❌ Erro ao carregar totais do AsyncStorage:', storageError);
      }

      return {};
    }
  };

  // Verificar conexão com o servidor
  const checkServerConnection = async () => {
    setCheckingConnection(true);
    try {
      const health = await api.checkServerHealth();
      setServerStatus(health.status === 'online' ? 'online' : 'offline');
      console.log('🔄 Status do servidor:', health);
      return health.status === 'online';
    } catch (error) {
      console.error('❌ Erro ao verificar conexão com servidor:', error);
      setServerStatus('offline');
      return false;
    } finally {
      setCheckingConnection(false);
    }
  };

  // Verificar se uma questão é essencial
  const isEssentialQuestion = async (formId, questionId) => {
    try {
      // Tentar buscar primeiro de formulários customizados
      const customForms = await StorageManager.loadCustomForms();
      const form = customForms.find(f => f.id === formId);

      if (form && form.requirements) {
        const question = form.requirements.find(
          q => q.id === questionId || q.codigo === questionId
        );
        if (question) {
          return question.isEssential || false;
        }
      }

      // Se não encontrar como customizado, tentar como fixo
      const formData = getFormData(formId);
      if (!formData) return false;

      // Buscar a pergunta específica em todas as seções
      for (const section of formData.sections || []) {
        for (const question of section.questions || []) {
          if (question.id === questionId || question.codigo === questionId) {
            return question.isEssential || false;
          }
        }
      }

      return false;
    } catch (error) {
      console.error('❌ Erro ao verificar questão essencial:', error);
      return false;
    }
  };

  // Analisar respostas com contagem correta
  const getAnswerAnalysis = async (answers, formId) => {
    if (!answers || Object.keys(answers).length === 0) {
      return {
        conforme: 0,
        naoConforme: 0,
        naoSeAplica: 0,
        conformeRE: 0,
        naoConformeRE: 0,
        naoSeAplicaRE: 0,
        conformeNaoRE: 0,
        naoConformeNaoRE: 0,
        naoSeAplicaNaoRE: 0,
        total: 0,
      };
    }

    // Contadores
    let conforme = 0,
      naoConforme = 0,
      naoSeAplica = 0;
    let conformeRE = 0,
      naoConformeRE = 0,
      naoSeAplicaRE = 0;
    let conformeNaoRE = 0,
      naoConformeNaoRE = 0,
      naoSeAplicaNaoRE = 0;

    // Processar cada resposta
    for (const [questionId, answer] of Object.entries(answers)) {
      const isRE = await isEssentialQuestion(formId, questionId);

      switch (answer) {
        case 'conforme':
          conforme++;
          if (isRE) {
            conformeRE++;
          } else {
            conformeNaoRE++;
          }
          break;
        case 'nao-conforme':
          naoConforme++;
          if (isRE) {
            naoConformeRE++;
          } else {
            naoConformeNaoRE++;
          }
          break;
        case 'nao-se-aplica':
          naoSeAplica++;
          if (isRE) {
            naoSeAplicaRE++;
          } else {
            naoSeAplicaNaoRE++;
          }
          break;
      }
    }

    return {
      conforme,
      naoConforme,
      naoSeAplica,
      conformeRE,
      naoConformeRE,
      naoSeAplicaRE,
      conformeNaoRE,
      naoConformeNaoRE,
      naoSeAplicaNaoRE,
      total: conforme + naoConforme + naoSeAplica,
    };
  };

  // Mostrar dialogs
  const showDialog = (title, message, actions = []) => {
    const defaultActions = [
      { text: '❌ Cancelar', action: () => setDialogVisible(false) },
      { text: '✅ Confirmar', action: () => setDialogVisible(false) },
    ];

    setDialogContent({
      title,
      message,
      actions: actions.length > 0 ? actions : defaultActions,
    });
    setDialogVisible(true);
  };

  // Ordenar respostas por status e data
  const sortResponses = responses => {
    return responses.sort((a, b) => {
      // Primeiro critério: Status (ordem de prioridade)
      const statusOrder = {
        'cloud-newer': 1,
        'local-newer': 2,
        'local-only': 3,
        synced: 4,
      };

      const statusA = statusOrder[a.syncStatus] || 10;
      const statusB = statusOrder[b.syncStatus] || 10;

      if (statusA !== statusB) {
        return statusA - statusB;
      }

      // Segundo critério: Data (mais recentes primeiro)
      const aDate = new Date(a.lastModified || a.createdAt);
      const bDate = new Date(b.lastModified || b.createdAt);
      return bDate - aDate;
    });
  };

  // Limpar todas as respostas
  const clearAllResponses = () => {
    const totalResponses = responses.length;
    const pendingCount = responses.filter(r => r.syncStatus !== 'synced').length;
    const syncedCount = responses.filter(r => r.syncStatus === 'synced').length;

    if (totalResponses === 0) {
      showDialog('ℹ️ Informação', 'Não há respostas para excluir.', [
        { text: '✅ OK', action: () => setDialogVisible(false) },
      ]);
      return;
    }

    showDialog(
      '🗑️ ATENÇÃO: Limpeza Geral',
      `⚠️ ESTA AÇÃO VAI EXCLUIR PERMANENTEMENTE:\n\n` +
        `• ${pendingCount} resposta(s) pendente(s) de envio\n` +
        `• ${syncedCount} resposta(s) já enviada(s)\n` +
        `• TOTAL: ${totalResponses} resposta(s)\n\n` +
        `💀 Esta operação NÃO PODE SER DESFEITA!\n\n` +
        `Tem certeza absoluta que deseja prosseguir?`,
      [
        { text: '❌ Cancelar', action: () => setDialogVisible(false) },
        {
          text: '🗑️ SIM, EXCLUIR TUDO',
          action: async () => {
            try {
              // Para respostas sincronizadas, tentar excluir também no MongoDB
              const syncedResponses = responses.filter(r => r.syncStatus === 'synced');

              // Verificar conexão antes de tentar excluir remotamente
              const isOnline = await checkServerConnection();

              if (isOnline) {
                for (const response of syncedResponses) {
                  try {
                    await api.deleteResponse(response.id);
                    console.log(`✅ Resposta ${response.id} excluída do MongoDB`);
                  } catch (apiError) {
                    console.error(
                      `❌ Erro ao excluir resposta ${response.id} do MongoDB:`,
                      apiError
                    );
                  }
                }
              } else {
                console.log('⚠️ Sem conexão com o servidor, excluindo apenas localmente');
              }

              // Limpar APENAS as respostas do avaliador atual
              const allResponses = await StorageManager.loadFormResponses();
              const otherResponses = allResponses.filter(
                r => r.avaliador && r.avaliador.id !== currentAvaliador.id
              );

              // Salvar apenas as respostas dos outros avaliadores
              await StorageManager.saveFormResponses(otherResponses);

              // Recarregar (que ficará vazio para o avaliador atual)
              setResponses([]);

              showDialog(
                '✅ Limpeza Concluída',
                `🧹 Todas as ${totalResponses} resposta(s) foram removidas permanentemente do dispositivo.\n\n` +
                  `O histórico foi completamente limpo. Você pode começar novamente!`,
                [
                  {
                    text: '🏠 Voltar ao Início',
                    action: () => {
                      setDialogVisible(false);
                      onBack();
                    },
                  },
                  {
                    text: '📋 Ficar Aqui',
                    action: () => setDialogVisible(false),
                  },
                ]
              );
            } catch (error) {
              console.error('❌ Erro durante limpeza geral:', error);
              showDialog(
                '❌ Erro na Limpeza',
                'Ocorreu um erro ao tentar limpar todas as respostas. Tente novamente.',
                [{ text: '✅ OK', action: () => setDialogVisible(false) }]
              );
            }
          },
        },
      ]
    );
  };

  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Enviar resposta ao MongoDB - VERSÃO CORRIGIDA COM VALIDAÇÕES DE SEGURANÇA
  const uploadResponse = async responseId => {
    setUploading(true);
    setCurrentUploadId(responseId);
    try {
      // Verificar conexão primeiro
      const isOnline = await checkServerConnection();

      if (!isOnline) {
        throw new Error('Sem conexão com o servidor. Verifique sua internet e tente novamente.');
      }

      // Buscar a resposta para enviar
      const responseToUpload = responses.find(r => r.id === responseId);
      if (!responseToUpload) {
        throw new Error(`Resposta com ID ${responseId} não encontrada`);
      }

      // VALIDAÇÃO DE SEGURANÇA
      if (responseToUpload.avaliador && responseToUpload.avaliador.id !== currentAvaliador.id) {
        console.error('❌ ERRO DE SEGURANÇA: Tentando enviar resposta de outro avaliador!');
        showDialog(
          '❌ Erro de Segurança',
          'Você não pode modificar respostas de outro avaliador.',
          [{ text: '✅ OK', action: () => setDialogVisible(false) }]
        );
        return;
      }

      // Verificar se o formulário ainda existe
      try {
        const formExists = await api.getFormById(responseToUpload.formId);
        if (!formExists) {
          throw new Error(`O formulário ${responseToUpload.formId} não existe mais no servidor`);
        }
      } catch (formError) {
        await StorageManager.removeResponse(responseId);
        setResponses(prev => prev.filter(r => r.id !== responseId));
        throw new Error(
          `O formulário associado a esta resposta não existe mais no servidor. A resposta foi removida localmente.`
        );
      }

      // Preparar resposta com avaliador correto
      const responseWithAvaliador = {
        ...responseToUpload,
        avaliador: currentAvaliador,
        totalQuestions:
          responseToUpload.totalQuestions || formTotalQuestions[responseToUpload.formId] || 0,
      };

      console.log(`📤 Enviando resposta:`, {
        id: responseWithAvaliador.id,
        formId: responseWithAvaliador.formId,
        avaliadorId: currentAvaliador.id,
      });

      // Enviar para o servidor
      const apiResponse = await api.saveResponse(responseWithAvaliador);

      // CORREÇÃO CRÍTICA: Atualizar com status sincronizado CORRETO
      const updatedResponse = {
        ...apiResponse,
        synced: true,
        syncedAt: new Date().toISOString(), // IMPORTANTE: Data ATUAL de sincronização
        syncStatus: 'synced',
        cloudVersion: null,
        avaliador: apiResponse.avaliador || responseWithAvaliador.avaliador,
        lastModified: new Date().toISOString(), // IMPORTANTE: Atualizar lastModified
      };

      // Salvar localmente com status atualizado
      await StorageManager.saveResponse(updatedResponse);

      // CORREÇÃO: Forçar atualização do cache da API
      try {
        await AsyncStorage.setItem(
          `cached_response_${updatedResponse.id}`,
          JSON.stringify(updatedResponse)
        );
      } catch (cacheError) {
        console.warn('⚠️ Erro ao atualizar cache:', cacheError);
      }

      // Atualizar estado local
      setResponses(prev =>
        prev.map(response => (response.id === responseId ? updatedResponse : response))
      );

      // CORREÇÃO: Notificar mudança para atualizar outras telas
      try {
        StorageManager.notifyChangeListeners('response_synced', {
          formId: updatedResponse.formId,
          responseId: updatedResponse.id,
          synced: true,
          syncedAt: updatedResponse.syncedAt,
          avaliadorId: currentAvaliador.id,
        });
      } catch (notifyError) {
        console.warn('⚠️ Erro ao notificar listeners:', notifyError);
      }

      showDialog(
        '✅ Envio Concluído',
        '📤 Formulário enviado com sucesso para o servidor!\n\nOs dados foram sincronizados e estão disponíveis para consulta no sistema web.',
        [{ text: '✅ OK', action: () => setDialogVisible(false) }]
      );

      // Enviar anexos
      if (responseWithAvaliador.attachments) {
        try {
          await uploadAttachments(responseWithAvaliador);
        } catch (attachError) {
          console.warn('⚠️ Erro ao enviar anexos:', attachError);
        }
      }

      // CORREÇÃO: Forçar reload completo após 1 segundo
      setTimeout(() => {
        loadResponses();
      }, 1000);
    } catch (error) {
      console.error('❌ Erro no envio:', error);
      showDialog(
        '❌ Erro no Envio',
        `Falha ao enviar formulário: ${error.message || 'Erro desconhecido'}. Verifique sua conexão e tente novamente.`,
        [{ text: '✅ OK', action: () => setDialogVisible(false) }]
      );
    } finally {
      setUploading(false);
      setCurrentUploadId(null);
    }
  };
  const downloadCloudVersion = async response => {
    try {
      // Verificar conexão primeiro
      const isOnline = await checkServerConnection();

      if (!isOnline) {
        showDialog(
          '❌ Sem Conexão',
          'Não é possível baixar a versão da nuvem sem conexão com o servidor.',
          [{ text: '✅ OK', action: () => setDialogVisible(false) }]
        );
        return;
      }

      // Se temos cloudVersion no objeto, usar diretamente
      if (response.cloudVersion) {
        const cloudResp = {
          ...response.cloudVersion,
          synced: true,
          syncedAt:
            response.cloudVersion.syncedAt ||
            response.cloudVersion.lastModified ||
            new Date().toISOString(),
          syncStatus: 'synced',
          totalQuestions:
            response.cloudVersion.totalQuestions ||
            response.totalQuestions ||
            formTotalQuestions[response.formId] ||
            0,
        };

        // Salvar no armazenamento local
        await StorageManager.saveResponse(cloudResp);

        // Atualizar o estado local
        setResponses(prev =>
          prev.map(r =>
            r.formId === response.formId && r.avaliador?.id === currentAvaliador.id ? cloudResp : r
          )
        );

        showDialog(
          '✅ Download Concluído',
          'A versão mais recente da nuvem foi baixada com sucesso e está pronta para uso.',
          [{ text: '✅ OK', action: () => setDialogVisible(false) }]
        );
      } else {
        // Buscar diretamente do servidor
        const cloudVersion = await api.getResponseById(response.id);

        if (!cloudVersion) {
          throw new Error('Não foi possível encontrar a versão na nuvem.');
        }

        const cloudResp = {
          ...cloudVersion,
          synced: true,
          syncedAt: cloudVersion.syncedAt || cloudVersion.lastModified || new Date().toISOString(),
          syncStatus: 'synced',
          totalQuestions:
            cloudVersion.totalQuestions ||
            response.totalQuestions ||
            formTotalQuestions[response.formId] ||
            0,
        };

        // Salvar no armazenamento local
        await StorageManager.saveResponse(cloudResp);

        // Atualizar o estado local
        setResponses(prev =>
          prev.map(r =>
            r.formId === response.formId && r.avaliador?.id === currentAvaliador.id ? cloudResp : r
          )
        );

        showDialog(
          '✅ Download Concluído',
          'A versão mais recente da nuvem foi baixada com sucesso e está pronta para uso.',
          [{ text: '✅ OK', action: () => setDialogVisible(false) }]
        );
      }
    } catch (error) {
      console.error('❌ Erro ao baixar versão da nuvem:', error);
      showDialog(
        '❌ Erro no Download',
        `Falha ao baixar versão da nuvem: ${error.message || 'Erro desconhecido'}. Verifique sua conexão e tente novamente.`,
        [{ text: '✅ OK', action: () => setDialogVisible(false) }]
      );
    }
  };

  // Enviar anexos ao Supabase
  const uploadAttachments = async response => {
    const attachments = response.attachments;
    if (!attachments) return;

    // Contador para log
    let successCount = 0;
    let failCount = 0;

    // Para cada pergunta com anexos
    for (const questionId in attachments) {
      const questionAttachments = attachments[questionId];

      // Para cada anexo da pergunta
      for (const attachment of questionAttachments) {
        // Pular anexos que já estão no Supabase
        if (attachment.supabasePath && attachment.supabaseUrl) {
          console.log(`📎 Anexo já está no Supabase: ${attachment.name}`);
          successCount++;
          continue;
        }

        // Apenas anexos com URL podem ser enviados
        if (!attachment.url) {
          console.warn(`⚠️ Anexo sem URL, pulando: ${attachment.name}`);
          failCount++;
          continue;
        }

        try {
          // Preparar o anexo para upload
          const fileToUpload = {
            url: attachment.url,
            name: attachment.name,
            type: attachment.mimeType || attachment.type,
            size: attachment.size,
          };

          // Pasta no Supabase: respostas/[formId]/[responseId]
          const folderPath = `respostas/${response.formId}/${response.id}`;

          // Enviar para o Supabase
          console.log(`📤 Enviando anexo para Supabase: ${attachment.name}`);
          const result = await api.uploadFile(fileToUpload, 'avaqualisattachment', folderPath);

          // Atualizar o anexo com os dados do Supabase
          attachment.supabasePath = result.path;
          attachment.supabaseUrl = result.url;
          attachment.supabaseBucket = result.bucket;

          successCount++;
          console.log(`✅ Anexo enviado com sucesso: ${attachment.name}`);
        } catch (error) {
          console.error(`❌ Erro ao enviar anexo ${attachment.name}:`, error);
          failCount++;
        }
      }
    }

    // Se houve alterações nos anexos, atualizar a resposta
    if (successCount > 0) {
      // Atualizar no armazenamento local
      await StorageManager.saveResponse(response);

      // Atualizar no MongoDB para refletir os novos caminhos dos anexos
      const apiData = transformResponseToApi(response);
      await api.saveResponse(apiData);

      console.log(`📊 Resumo do envio de anexos: ${successCount} sucesso, ${failCount} falhas`);
    }
  };

  // Resolver conflito usando versão local
  const resolveConflictWithLocal = async () => {
    try {
      if (!localVersion) return;

      // Fechar o modal de conflito
      setConflictModalVisible(false);

      setUploading(true);

      // VALIDAÇÃO DE SEGURANÇA: Verificar se não está tentando sobrescrever resposta de outro avaliador
      if (localVersion.avaliador && localVersion.avaliador.id !== currentAvaliador.id) {
        console.error(
          '❌ ERRO DE SEGURANÇA: Tentando resolver conflito com resposta de outro avaliador!'
        );

        showDialog(
          '❌ Erro de Segurança',
          'Você não pode modificar respostas de outro avaliador.',
          [{ text: '✅ OK', action: () => setDialogVisible(false) }]
        );

        setUploading(false);
        return;
      }

      // Verificar se o formulário ainda existe no servidor
      try {
        console.log(
          `🔍 Verificando se o formulário ${localVersion.formId} ainda existe no servidor...`
        );
        const formExists = await api.getFormById(localVersion.formId);

        if (!formExists) {
          // Remover resposta local, já que o formulário não existe mais
          await StorageManager.removeResponse(localVersion.id);

          // Atualizar a lista de respostas
          setResponses(prev => prev.filter(r => r.id !== localVersion.id));

          throw new Error(
            `O formulário associado a esta resposta não existe mais no servidor. A resposta foi removida localmente.`
          );
        }

        console.log(`✅ Formulário ${localVersion.formId} verificado e existe no servidor`);
      } catch (formError) {
        console.error(`❌ Erro ao verificar formulário:`, formError);

        showDialog(
          '❌ Erro no Envio',
          `O formulário associado a esta resposta não existe mais no servidor. A resposta foi removida localmente.`,
          [{ text: '✅ OK', action: () => setDialogVisible(false) }]
        );

        setUploading(false);
        return;
      }

      // Garantir que o avaliador correto esteja na resposta
      const responseWithAvaliador = {
        ...localVersion,
        avaliador: currentAvaliador,
      };

      // Enviar para o MongoDB usando a API
      const apiResponse = await api.saveResponse(responseWithAvaliador);

      // Atualizar a resposta com status sincronizado
      const updatedResponse = {
        ...localVersion,
        synced: true,
        syncedAt: new Date().toISOString(),
        syncStatus: 'synced',
        cloudVersion: null, // Limpar referência à versão da nuvem
        avaliador: currentAvaliador,
      };

      // Atualizar no armazenamento local
      await StorageManager.saveResponse(updatedResponse);

      // Atualizar o estado local
      setResponses(prev =>
        prev.map(response =>
          response.formId === localVersion.formId && response.avaliador?.id === currentAvaliador.id
            ? updatedResponse
            : response
        )
      );

      showDialog(
        '✅ Conflito Resolvido',
        'Sua versão local foi enviada para o servidor, substituindo a versão anterior.',
        [{ text: '✅ OK', action: () => setDialogVisible(false) }]
      );

      // Também enviar anexos, se houver
      if (localVersion.attachments) {
        try {
          await uploadAttachments(localVersion);
        } catch (attachError) {
          console.error('⚠️ Erro ao enviar anexos:', attachError);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao resolver conflito com versão local:', error);
      showDialog(
        '❌ Erro na Resolução',
        `Falha ao resolver conflito: ${error.message || 'Erro desconhecido'}.`,
        [{ text: '✅ OK', action: () => setDialogVisible(false) }]
      );
    } finally {
      setUploading(false);
    }
  };

  // Resolver conflito usando versão da nuvem
  const resolveConflictWithCloud = async () => {
    try {
      if (!cloudVersion || !localVersion) return;

      // Fechar o modal de conflito
      setConflictModalVisible(false);

      // Verificar se o formulário ainda existe no servidor
      try {
        console.log(
          `🔍 Verificando se o formulário ${cloudVersion.formId} ainda existe no servidor...`
        );
        const formExists = await api.getFormById(cloudVersion.formId);

        if (!formExists) {
          // Remover resposta local, já que o formulário não existe mais
          await StorageManager.removeResponse(cloudVersion.id);

          // Atualizar a lista de respostas
          setResponses(prev => prev.filter(r => r.id !== cloudVersion.id));

          throw new Error(
            `O formulário associado a esta resposta não existe mais no servidor. A resposta foi removida localmente.`
          );
        }

        console.log(`✅ Formulário ${cloudVersion.formId} verificado e existe no servidor`);
      } catch (formError) {
        console.error(`❌ Erro ao verificar formulário:`, formError);

        showDialog(
          '❌ Erro na Resolução',
          `O formulário associado a esta resposta não existe mais no servidor. A resposta foi removida localmente.`,
          [{ text: '✅ OK', action: () => setDialogVisible(false) }]
        );

        return;
      }

      // Atualizar a resposta local com a versão da nuvem
      const updatedResponse = {
        ...cloudVersion,
        synced: true,
        syncedAt: cloudVersion.syncedAt || new Date().toISOString(),
        syncStatus: 'synced',
        cloudVersion: null,
      };

      // Atualizar no armazenamento local
      await StorageManager.saveResponse(updatedResponse);

      // Atualizar o estado local
      setResponses(prev =>
        prev.map(response =>
          response.formId === cloudVersion.formId && response.avaliador?.id === currentAvaliador.id
            ? updatedResponse
            : response
        )
      );

      showDialog('✅ Conflito Resolvido', 'A versão da nuvem foi restaurada em seu dispositivo.', [
        { text: '✅ OK', action: () => setDialogVisible(false) },
      ]);
    } catch (error) {
      console.error('❌ Erro ao resolver conflito com versão da nuvem:', error);
      showDialog(
        '❌ Erro na Resolução',
        `Falha ao resolver conflito: ${error.message || 'Erro desconhecido'}.`,
        [{ text: '✅ OK', action: () => setDialogVisible(false) }]
      );
    }
  };

  // Visualizar versão da nuvem
  const viewCloudVersion = async response => {
    try {
      // Verificar conexão primeiro
      const isOnline = await checkServerConnection();

      if (!isOnline) {
        showDialog(
          '❌ Sem Conexão',
          'Não é possível visualizar a versão da nuvem sem conexão com o servidor.',
          [{ text: '✅ OK', action: () => setDialogVisible(false) }]
        );
        return;
      }

      // Se já temos cloudVersion no objeto, usar diretamente
      if (response.cloudVersion) {
        setLocalVersion(response);
        setCloudVersion(response.cloudVersion);
        setConflictModalVisible(true);
        return;
      }

      // Verificar se o formulário ainda existe no servidor
      try {
        console.log(
          `🔍 Verificando se o formulário ${response.formId} ainda existe no servidor...`
        );
        const formExists = await api.getFormById(response.formId);

        if (!formExists) {
          // Remover resposta local, já que o formulário não existe mais
          await StorageManager.removeResponse(response.id);

          // Atualizar a lista de respostas
          setResponses(prev => prev.filter(r => r.id !== response.id));

          throw new Error(
            `O formulário associado a esta resposta não existe mais no servidor. A resposta foi removida localmente.`
          );
        }

        console.log(`✅ Formulário ${response.formId} verificado e existe no servidor`);
      } catch (formError) {
        console.error(`❌ Erro ao verificar formulário:`, formError);

        showDialog(
          '❌ Erro na Visualização',
          `O formulário associado a esta resposta não existe mais no servidor. A resposta foi removida localmente.`,
          [{ text: '✅ OK', action: () => setDialogVisible(false) }]
        );
        return;
      }

      // Buscar a versão da nuvem
      const cloudResponse = await api.getResponseById(response.id);

      if (!cloudResponse) {
        showDialog(
          '❌ Não Encontrado',
          'A versão da nuvem não foi encontrada. Ela pode ter sido excluída.',
          [{ text: '✅ OK', action: () => setDialogVisible(false) }]
        );
        return;
      }

      // Garantir que a versão da nuvem tenha totalQuestions
      const cloudResponseWithTotal = {
        ...cloudResponse,
        totalQuestions:
          cloudResponse.totalQuestions ||
          response.totalQuestions ||
          formTotalQuestions[response.formId] ||
          0,
      };

      // Mostrar comparação no modal de conflito
      setLocalVersion(response);
      setCloudVersion(cloudResponseWithTotal);
      setConflictModalVisible(true);
    } catch (error) {
      console.error('❌ Erro ao visualizar versão da nuvem:', error);
      showDialog(
        '❌ Erro',
        `Falha ao buscar versão da nuvem: ${error.message || 'Erro desconhecido'}.`,
        [{ text: '✅ OK', action: () => setDialogVisible(false) }]
      );
    }
  };

  // Excluir resposta
  const deleteResponse = (responseId, formTitle) => {
    // Buscar a resposta para identificar seu status
    const responseToDelete = responses.find(r => r.id === responseId);

    if (!responseToDelete) {
      console.warn(`⚠️ Resposta ${responseId} não encontrada para exclusão`);
      return;
    }

    // VALIDAÇÃO DE SEGURANÇA: Verificar se é realmente do avaliador atual
    if (responseToDelete.avaliador && responseToDelete.avaliador.id !== currentAvaliador.id) {
      console.error('❌ ERRO DE SEGURANÇA: Tentando excluir resposta de outro avaliador!');

      showDialog(
        '❌ Erro de Segurança',
        'Você não pode excluir respostas de outro avaliador. Esta resposta será removida da sua visualização.',
        [
          {
            text: '✅ OK',
            action: () => {
              // Remover da visualização
              setResponses(prev => prev.filter(r => r.id !== responseId));
              setDialogVisible(false);
            },
          },
        ]
      );
      return;
    }

    // Personalizar mensagem com base no status
    let title, message, confirmText;

    if (responseToDelete.syncStatus === 'synced') {
      title = '🗑️ Confirmar Remoção Local';
      message =
        `Deseja remover a cópia local do formulário "${formTitle}"?\n\n` +
        `O formulário continuará disponível na nuvem, mas será removido deste dispositivo.\n\n` +
        `Você poderá baixá-lo novamente se necessário.`;
      confirmText = '🗑️ Remover Local';
    } else if (
      responseToDelete.syncStatus === 'cloud-newer' ||
      responseToDelete.syncStatus === 'local-newer'
    ) {
      title = '⚠️ Confirmar Ação';
      message =
        `Este formulário "${formTitle}" tem versões diferentes no dispositivo e na nuvem.\n\n` +
        `O que você deseja fazer?\n` +
        `• Remover apenas a versão local (a versão na nuvem será mantida)\n` +
        `• Excluir completamente (remover da nuvem e do dispositivo)`;

      // Ação personalizada para este caso específico
      showDialog(title, message, [
        { text: '❌ Cancelar', action: () => setDialogVisible(false) },
        {
          text: '🗑️ Remover Local',
          action: async () => {
            try {
              // Remover apenas localmente
              await StorageManager.removeResponse(responseId);

              // Atualizar o estado local
              setResponses(prev => prev.filter(r => r.id !== responseId));

              showDialog(
                '✅ Remoção Concluída',
                'A versão local foi removida. A versão na nuvem continua disponível.',
                [{ text: '✅ OK', action: () => setDialogVisible(false) }]
              );
            } catch (error) {
              console.error('❌ Erro ao remover resposta local:', error);
              showDialog('❌ Erro na Remoção', 'Erro ao remover resposta. Tente novamente.', [
                { text: '✅ OK', action: () => setDialogVisible(false) },
              ]);
            }
          },
        },
        {
          text: '🗑️ Excluir Tudo',
          action: async () => {
            try {
              // Verificar conexão
              const isOnline = await checkServerConnection();

              // Verificar se o formulário ainda existe no servidor
              let formularioExiste = false;
              if (isOnline) {
                try {
                  const formExists = await api.getFormById(responseToDelete.formId);
                  formularioExiste = !!formExists;
                } catch (formError) {
                  console.warn(
                    `⚠️ Formulário ${responseToDelete.formId} não existe mais no servidor`
                  );
                }
              }

              if (isOnline && formularioExiste) {
                try {
                  await api.deleteResponse(responseId);
                  console.log('✅ Resposta excluída do MongoDB:', responseId);
                } catch (apiError) {
                  console.error('❌ Erro ao excluir resposta do MongoDB:', apiError);
                  showDialog(
                    '⚠️ Atenção',
                    'Não foi possível excluir a versão na nuvem. A versão local será removida.',
                    [{ text: '✅ OK', action: () => setDialogVisible(false) }]
                  );
                }
              } else if (!formularioExiste && isOnline) {
                showDialog(
                  '⚠️ Atenção',
                  'O formulário não existe mais no servidor. Apenas a versão local será removida.',
                  [{ text: '✅ OK', action: () => setDialogVisible(false) }]
                );
              } else {
                showDialog(
                  '⚠️ Atenção',
                  'Sem conexão com o servidor. Apenas a versão local será removida.',
                  [{ text: '✅ OK', action: () => setDialogVisible(false) }]
                );
              }

              // Remover localmente
              await StorageManager.removeResponse(responseId);

              // Atualizar o estado local
              setResponses(prev => prev.filter(r => r.id !== responseId));

              showDialog('✅ Exclusão Concluída', 'Resposta excluída com sucesso.', [
                { text: '✅ OK', action: () => setDialogVisible(false) },
              ]);
            } catch (error) {
              console.error('❌ Erro ao excluir resposta:', error);
              showDialog('❌ Erro na Exclusão', 'Erro ao excluir resposta. Tente novamente.', [
                { text: '✅ OK', action: () => setDialogVisible(false) },
              ]);
            }
          },
        },
      ]);

      return; // Retornar aqui para não mostrar o diálogo padrão
    } else {
      title = '🗑️ Confirmar Exclusão';
      message = `Tem certeza que deseja excluir a resposta do formulário "${formTitle}"?\n\n⚠️ Esta ação não pode ser desfeita.`;
      confirmText = '🗑️ Excluir';
    }

    // Diálogo padrão para os outros casos
    showDialog(title, message, [
      { text: '❌ Cancelar', action: () => setDialogVisible(false) },
      {
        text: confirmText,
        action: async () => {
          try {
            // Se estiver sincronizada, tentar excluir do MongoDB também
            if (responseToDelete.syncStatus === 'synced') {
              // Verificar conexão
              const isOnline = await checkServerConnection();

              // Verificar se o formulário ainda existe no servidor
              let formularioExiste = false;
              if (isOnline) {
                try {
                  const formExists = await api.getFormById(responseToDelete.formId);
                  formularioExiste = !!formExists;
                } catch (formError) {
                  console.warn(
                    `⚠️ Formulário ${responseToDelete.formId} não existe mais no servidor`
                  );
                }
              }

              if (isOnline && formularioExiste) {
                try {
                  await api.deleteResponse(responseId);
                  console.log('✅ Resposta excluída do MongoDB:', responseId);
                } catch (apiError) {
                  console.error('❌ Erro ao excluir resposta do MongoDB:', apiError);
                  // Continuar com a exclusão local mesmo se falhar no servidor
                }
              }
            }

            // Excluir localmente usando StorageManager
            await StorageManager.removeResponse(responseId);

            // Atualizar o estado local
            setResponses(prev => prev.filter(r => r.id !== responseId));

            showDialog(
              '✅ Exclusão Concluída',
              responseToDelete.syncStatus === 'synced'
                ? 'Resposta removida do dispositivo com sucesso.'
                : 'Resposta excluída com sucesso.',
              [{ text: '✅ OK', action: () => setDialogVisible(false) }]
            );
          } catch (error) {
            console.error('❌ Erro ao excluir resposta:', error);
            showDialog('❌ Erro na Exclusão', 'Erro ao excluir resposta. Tente novamente.', [
              { text: '✅ OK', action: () => setDialogVisible(false) },
            ]);
          }
        },
      },
    ]);
  };

  // Gerar resumo da resposta
  const generateResponseSummary = async (answers, formId) => {
    // Usar Promise para resolver de forma assíncrona
    try {
      const analysis = await getAnswerAnalysis(answers, formId);

      if (analysis.total === 0) {
        return '📝 Nenhuma resposta registrada';
      }

      const totalValid = analysis.conforme + analysis.naoConforme;
      const totalValidRE = analysis.conformeRE + analysis.naoConformeRE;

      const percentConforme =
        totalValid > 0 ? ((analysis.conforme / totalValid) * 100).toFixed(2) : '0.00';
      const percentNaoConforme =
        totalValid > 0 ? ((analysis.naoConforme / totalValid) * 100).toFixed(2) : '0.00';

      const percentConformeRE =
        totalValidRE > 0 ? ((analysis.conformeRE / totalValidRE) * 100).toFixed(2) : '0.00';
      const percentNaoConformeRE =
        totalValidRE > 0 ? ((analysis.naoConformeRE / totalValidRE) * 100).toFixed(2) : '0.00';

      // Montar resumo
      const summary = `📋 Resumo das Respostas:
- ✅ Conforme (${analysis.conformeNaoRE}${analysis.conformeRE > 0 ? `+${analysis.conformeRE}⚠️RE` : ''} = ${analysis.conforme})
- ❌ Não Conforme (${analysis.naoConformeNaoRE}${analysis.naoConformeRE > 0 ? `+${analysis.naoConformeRE}⚠️RE` : ''} = ${analysis.naoConforme})
- ➖ Não se Aplica (${analysis.naoSeAplicaNaoRE}${analysis.naoSeAplicaRE > 0 ? `+${analysis.naoSeAplicaRE}⚠️RE` : ''} = ${analysis.naoSeAplica})
========================
🌐 Geral % ✅ Conforme = ${analysis.conforme}/${totalValid} = ${percentConforme}%
🌐 Geral % ❌ Não Conforme = ${analysis.naoConforme}/${totalValid} = ${percentNaoConforme}%
========================
${
  totalValidRE > 0
    ? `⚠️RE % ✅ Conforme = ${analysis.conformeRE}/${totalValidRE} = ${percentConformeRE}%
⚠️RE % ❌ Não Conforme = ${analysis.naoConformeRE}/${totalValidRE} = ${percentNaoConformeRE}%`
    : '⚠️RE: Nenhuma questão essencial respondida'
}`;

      return summary;
    } catch (error) {
      console.error('❌ Erro ao gerar resumo:', error);
      return '❌ Erro ao gerar resumo das respostas';
    }
  };

  // Filtrar respostas por status
  const getFilteredResponses = () => {
    if (activeFilter === 'all') {
      return responses;
    }

    if (activeFilter === 'synced') {
      return responses.filter(r => r.syncStatus === 'synced');
    }

    if (activeFilter === 'cloud-newer') {
      return responses.filter(r => r.syncStatus === 'cloud-newer');
    }

    if (activeFilter === 'local-newer') {
      return responses.filter(r => r.syncStatus === 'local-newer');
    }

    if (activeFilter === 'local-only') {
      return responses.filter(r => r.syncStatus === 'local-only');
    }

    return responses;
  };

  // Contadores para diferentes tipos de respostas
  const syncedCount = responses.filter(r => r.syncStatus === 'synced').length;
  const cloudNewerCount = responses.filter(r => r.syncStatus === 'cloud-newer').length;
  const localNewerCount = responses.filter(r => r.syncStatus === 'local-newer').length;
  const localOnlyCount = responses.filter(r => r.syncStatus === 'local-only').length;
  const pendingCount = cloudNewerCount + localNewerCount + localOnlyCount;

  // Obter respostas filtradas para exibição
  const filteredResponses = getFilteredResponses();

  // Enviar todas as respostas pendentes - VERSÃO CORRIGIDA COM VALIDAÇÕES DE SEGURANÇA
  const uploadAllResponses = async () => {
    // Filtrar apenas as respostas não sincronizadas (local-newer e local-only) E do avaliador atual
    const pendingResponses = responses.filter(
      r =>
        (r.syncStatus === 'local-newer' || r.syncStatus === 'local-only') &&
        r.avaliador &&
        r.avaliador.id === currentAvaliador.id
    );

    if (pendingResponses.length === 0) {
      showDialog('ℹ️ Informação', 'Não há formulários para enviar.', [
        { text: '✅ OK', action: () => setDialogVisible(false) },
      ]);
      return;
    }

    // Verificar conexão primeiro
    const isOnline = await checkServerConnection();

    if (!isOnline) {
      showDialog(
        '❌ Sem Conexão',
        'Não foi possível conectar ao servidor. Verifique sua conexão com a internet e tente novamente.',
        [{ text: '✅ OK', action: () => setDialogVisible(false) }]
      );
      return;
    }

    // Mensagem personalizada com base nos tipos de respostas
    const localNewerResponses = pendingResponses.filter(r => r.syncStatus === 'local-newer');
    const localOnlyResponses = pendingResponses.filter(r => r.syncStatus === 'local-only');

    let message = '';
    if (localNewerResponses.length > 0 && localOnlyResponses.length > 0) {
      message =
        `Deseja enviar ${pendingResponses.length} formulário(s) para o servidor?\n\n` +
        `• ${localOnlyResponses.length} novo(s) formulário(s)\n` +
        `• ${localNewerResponses.length} formulário(s) modificado(s) que substituirão versões na nuvem`;
    } else if (localNewerResponses.length > 0) {
      message =
        `Deseja enviar ${localNewerResponses.length} formulário(s) modificado(s) para o servidor?\n\n` +
        `Isso substituirá as versões existentes na nuvem.`;
    } else {
      message = `Deseja enviar ${localOnlyResponses.length} novo(s) formulário(s) para o servidor?`;
    }

    showDialog('📤 Confirmar Envio', message, [
      { text: '❌ Cancelar', action: () => setDialogVisible(false) },
      {
        text: '📤 Enviar Todos',
        action: async () => {
          setUploading(true);

          // Contador para feedback
          let successCount = 0;
          let errorCount = 0;
          let securityErrorCount = 0;

          try {
            // Processar cada resposta
            for (const response of pendingResponses) {
              try {
                // VALIDAÇÃO DE SEGURANÇA: Double-check do avaliador
                if (!response.avaliador || response.avaliador.id !== currentAvaliador.id) {
                  console.error(
                    `❌ ERRO DE SEGURANÇA: Resposta ${response.id} não pertence ao avaliador atual!`
                  );
                  securityErrorCount++;
                  continue;
                }

                // Verificar novamente se o formulário existe
                try {
                  const formExists = await api.getFormById(response.formId);
                  if (!formExists) {
                    console.warn(
                      `⚠️ Formulário ${response.formId} não existe mais no servidor, pulando resposta ${response.id}`
                    );
                    await StorageManager.removeResponse(response.id);
                    continue;
                  }
                } catch (formError) {
                  console.error(`❌ Erro ao verificar formulário ${response.formId}:`, formError);
                  await StorageManager.removeResponse(response.id);
                  continue;
                }

                // Garantir que a resposta tenha dados do avaliador correto
                const responseWithAvaliador = {
                  ...response,
                  avaliador: currentAvaliador, // Usar o avaliador logado
                  totalQuestions:
                    response.totalQuestions || formTotalQuestions[response.formId] || 0,
                };

                console.log(`📤 Enviando resposta em lote:`, {
                  id: response.id,
                  formId: response.formId,
                  avaliadorId: currentAvaliador.id,
                  syncStatus: response.syncStatus,
                });

                // Enviar para o MongoDB usando a API
                const apiResponse = await api.saveResponse(responseWithAvaliador);

                // Atualizar localmente
                const updatedResponse = {
                  ...apiResponse,
                  synced: true,
                  syncedAt: new Date().toISOString(),
                  syncStatus: 'synced',
                  cloudVersion: null,
                  // Garantir que o avaliador esteja presente
                  avaliador: apiResponse.avaliador || responseWithAvaliador.avaliador,
                };

                // Salvar no armazenamento local
                await StorageManager.saveResponse(updatedResponse);

                // Tentar enviar anexos
                try {
                  await uploadAttachments(updatedResponse);
                } catch (attachError) {
                  console.error(
                    `⚠️ Erro ao enviar anexos da resposta ${response.id}:`,
                    attachError
                  );
                  // Continuar mesmo com erro de anexos
                }

                successCount++;
              } catch (responseError) {
                console.error(`❌ Erro ao enviar resposta ${response.id}:`, responseError);
                errorCount++;
              }
            }

            // Recarregar as respostas para mostrar o status atualizado
            await loadResponses();

            // Mostrar resultado
            if (errorCount === 0 && securityErrorCount === 0) {
              showDialog(
                '✅ Envio Concluído',
                `📤 ${successCount} formulário(s) enviado(s) com sucesso para o servidor!`,
                [{ text: '✅ OK', action: () => setDialogVisible(false) }]
              );
            } else {
              let errorMessage = `📤 ${successCount} formulário(s) enviado(s) com sucesso.`;

              if (errorCount > 0) {
                errorMessage += `\n❌ ${errorCount} formulário(s) com erro de envio.`;
              }

              if (securityErrorCount > 0) {
                errorMessage += `\n🔒 ${securityErrorCount} formulário(s) ignorado(s) por questões de segurança.`;
              }

              errorMessage +=
                '\n\nVerifique sua conexão e tente novamente para os formulários com erro.';

              showDialog('⚠️ Envio Parcial', errorMessage, [
                { text: '✅ OK', action: () => setDialogVisible(false) },
              ]);
            }
          } catch (error) {
            console.error('❌ Erro no envio em lote:', error);
            showDialog('❌ Erro no Envio', 'Falha ao enviar formulários. Verifique sua conexão.', [
              { text: '✅ OK', action: () => setDialogVisible(false) },
            ]);
          } finally {
            setUploading(false);
          }
        },
      },
    ]);
  };

  // Baixar todas as versões mais recentes da nuvem
  const downloadAllNewerVersions = async () => {
    // Filtrar apenas as respostas com versão da nuvem mais recente E do avaliador atual
    const cloudNewerResponses = responses.filter(
      r => r.syncStatus === 'cloud-newer' && r.avaliador && r.avaliador.id === currentAvaliador.id
    );

    if (cloudNewerResponses.length === 0) {
      showDialog('ℹ️ Informação', 'Não há formulários desatualizados para baixar.', [
        { text: '✅ OK', action: () => setDialogVisible(false) },
      ]);
      return;
    }

    // Verificar conexão primeiro
    const isOnline = await checkServerConnection();

    if (!isOnline) {
      showDialog(
        '❌ Sem Conexão',
        'Não foi possível conectar ao servidor. Verifique sua conexão com a internet e tente novamente.',
        [{ text: '✅ OK', action: () => setDialogVisible(false) }]
      );
      return;
    }

    showDialog(
      '📥 Confirmar Download',
      `Deseja baixar as versões mais recentes de ${cloudNewerResponses.length} formulário(s)?\n\n` +
        `Isso substituirá suas versões locais pelas versões mais atualizadas da nuvem.`,
      [
        { text: '❌ Cancelar', action: () => setDialogVisible(false) },
        {
          text: '📥 Baixar Todos',
          action: async () => {
            setUploading(true);

            // Contador para feedback
            let successCount = 0;
            let errorCount = 0;

            try {
              // Processar cada resposta
              for (const response of cloudNewerResponses) {
                try {
                  // Verificar novamente se o formulário existe
                  try {
                    const formExists = await api.getFormById(response.formId);
                    if (!formExists) {
                      console.warn(
                        `⚠️ Formulário ${response.formId} não existe mais no servidor, pulando resposta ${response.id}`
                      );
                      await StorageManager.removeResponse(response.id);
                      continue;
                    }
                  } catch (formError) {
                    console.error(`❌ Erro ao verificar formulário ${response.formId}:`, formError);
                    await StorageManager.removeResponse(response.id);
                    continue;
                  }

                  // Se temos cloudVersion no objeto, usar diretamente
                  if (response.cloudVersion) {
                    const cloudResp = {
                      ...response.cloudVersion,
                      synced: true,
                      syncedAt:
                        response.cloudVersion.syncedAt ||
                        response.cloudVersion.lastModified ||
                        new Date().toISOString(),
                      syncStatus: 'synced',
                      cloudVersion: null,
                      totalQuestions:
                        response.cloudVersion.totalQuestions ||
                        response.totalQuestions ||
                        formTotalQuestions[response.formId] ||
                        0,
                    };

                    // Salvar no armazenamento local
                    await StorageManager.saveResponse(cloudResp);
                    successCount++;
                  } else {
                    // Buscar diretamente do servidor
                    const cloudVersion = await api.getResponseById(response.id);

                    if (!cloudVersion) {
                      throw new Error(
                        `Não foi possível encontrar a versão na nuvem para ${response.id}`
                      );
                    }

                    const cloudResp = {
                      ...cloudVersion,
                      synced: true,
                      syncedAt:
                        cloudVersion.syncedAt ||
                        cloudVersion.lastModified ||
                        new Date().toISOString(),
                      syncStatus: 'synced',
                      cloudVersion: null,
                      totalQuestions:
                        cloudVersion.totalQuestions ||
                        response.totalQuestions ||
                        formTotalQuestions[response.formId] ||
                        0,
                    };

                    // Salvar no armazenamento local
                    await StorageManager.saveResponse(cloudResp);
                    successCount++;
                  }
                } catch (responseError) {
                  console.error(`❌ Erro ao baixar resposta ${response.id}:`, responseError);
                  errorCount++;
                }
              }

              // Recarregar as respostas para mostrar o status atualizado
              await loadResponses();

              // Mostrar resultado
              if (errorCount === 0) {
                showDialog(
                  '✅ Download Concluído',
                  `📥 ${successCount} formulário(s) baixado(s) com sucesso do servidor!`,
                  [{ text: '✅ OK', action: () => setDialogVisible(false) }]
                );
              } else {
                showDialog(
                  '⚠️ Download Parcial',
                  `📥 ${successCount} formulário(s) baixado(s) com sucesso.\n❌ ${errorCount} formulário(s) com erro de download.\n\nVerifique sua conexão e tente novamente para os formulários com erro.`,
                  [{ text: '✅ OK', action: () => setDialogVisible(false) }]
                );
              }
            } catch (error) {
              console.error('❌ Erro no download em lote:', error);
              showDialog(
                '❌ Erro no Download',
                'Falha ao baixar formulários. Verifique sua conexão.',
                [{ text: '✅ OK', action: () => setDialogVisible(false) }]
              );
            } finally {
              setUploading(false);
            }
          },
        },
      ]
    );
  };

  // onSelect para disparar atualização quando voltar
  const handleSelectForm = form => {
    // Chamar a função original passada como prop
    onSelectForm(form);

    // Marcar para recarregar na próxima vez que a tela ficar ativa
    setLastFocusTime(Date.now());
  };

  // Renderizar carregamento
  if (loading) {
    return (
      <View style={styles.container}>
        <Appbar.Header style={styles.appbar}>
          <Appbar.BackAction onPress={onBack} color={AVAQualisTheme.colors.onPrimary} />
          <Appbar.Content title="⏳ Carregando..." titleStyle={styles.appbarTitle} />
        </Appbar.Header>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={AVAQualisTheme.colors.primary} />
          <Text style={styles.loadingText}>📋 Carregando suas respostas...</Text>
        </View>
      </View>
    );
  }

  // Renderizar tela principal
  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar}>
        <Appbar.BackAction onPress={onBack} color={AVAQualisTheme.colors.onPrimary} />
        <Appbar.Content
          title="📁 Pasta de Uploads"
          subtitle={`${responses.length} formulário(s) • ${pendingCount > 0 ? `${pendingCount} pendente(s)` : 'Tudo sincronizado'}`}
          titleStyle={styles.appbarTitle}
          subtitleStyle={styles.appbarSubtitle}
        />
        <Appbar.Action
          icon="refresh"
          onPress={loadResponses}
          iconColor={AVAQualisTheme.colors.onPrimary}
        />
        <Appbar.Action
          icon="delete-sweep"
          onPress={clearAllResponses}
          iconColor={AVAQualisTheme.colors.onPrimary}
          disabled={responses.length === 0}
        />
      </Appbar.Header>

      {/* Status do Servidor */}
      <View style={styles.serverStatusContainer}>
        <Text style={styles.serverStatusLabel}>Status do Servidor:</Text>
        {checkingConnection ? (
          <View style={styles.serverStatusChecking}>
            <ActivityIndicator size="small" color={AVAQualisTheme.colors.primary} />
            <Text style={styles.serverStatusCheckingText}>Verificando conexão...</Text>
          </View>
        ) : (
          <View
            style={[
              styles.serverStatusIndicator,
              {
                backgroundColor:
                  serverStatus === 'online'
                    ? AVAQualisTheme.colors.success + '20'
                    : AVAQualisTheme.colors.error + '20',
              },
            ]}
          >
            <Text
              style={[
                styles.serverStatusText,
                {
                  color:
                    serverStatus === 'online'
                      ? AVAQualisTheme.colors.success
                      : AVAQualisTheme.colors.error,
                },
              ]}
            >
              {serverStatus === 'online' ? '✅ Conectado' : '❌ Desconectado'}
            </Text>
          </View>
        )}
        <Button
          mode="outlined"
          compact
          icon="refresh"
          onPress={checkServerConnection}
          style={styles.checkConnectionButton}
        >
          Verificar Conexão
        </Button>
      </View>

      {/* Avaliador Info */}
      {currentAvaliador && (
        <View style={styles.avaliadorInfoContainer}>
          <View style={styles.avaliadorInfoHeader}>
            <Text style={styles.avaliadorInfoTitle}>Avaliador Logado:</Text>
            <Chip
              style={styles.avaliadorInfoLevelChip}
              textStyle={styles.avaliadorInfoLevelChipText}
              mode="outlined"
            >
              {getLevelEmoji(currentAvaliador.level)} {getLevelDescription(currentAvaliador.level)}
            </Chip>
          </View>
          <Text style={styles.avaliadorInfoName}>{currentAvaliador.nome}</Text>
          <Text style={styles.avaliadorInfoEmail}>
            {currentAvaliador.email || currentAvaliador.login}
          </Text>
        </View>
      )}

      {/* Legenda de status */}
      {responses.length > 0 && (
        <View style={styles.legendContainer}>
          <Text style={styles.legendTitle}>Legenda de Status:</Text>
          <View style={styles.legendItems}>
            <View style={styles.legendItem}>
              <View style={[styles.legendColorBox, { backgroundColor: '#E8F5E9' }]} />
              <Text style={styles.legendText}>🟩 Verde: Tudo sincronizado</Text>
            </View>
            <View style={styles.legendItem}>
              <View style={[styles.legendColorBox, { backgroundColor: '#FFEBEE' }]} />
              <Text style={styles.legendText}>🟥 Vermelho: Precisa baixar da nuvem</Text>
            </View>
            <View style={styles.legendItem}>
              <View style={[styles.legendColorBox, { backgroundColor: '#FFF8E1' }]} />
              <Text style={styles.legendText}>🟧 Laranja: Precisa atualizar na nuvem</Text>
            </View>
            <View style={styles.legendItem}>
              <View style={[styles.legendColorBox, { backgroundColor: '#FFECB3' }]} />
              <Text style={styles.legendText}>🟨 Amarelo: Somente local</Text>
            </View>
          </View>
        </View>
      )}

      {/* Filtros de status */}
      {responses.length > 0 && (
        <View style={styles.filtersContainer}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.filtersScrollContent}
          >
            <TouchableOpacity
              style={[styles.filterButton, activeFilter === 'all' && styles.activeFilterButton]}
              onPress={() => setActiveFilter('all')}
            >
              <Text
                style={[
                  styles.filterButtonText,
                  activeFilter === 'all' && styles.activeFilterButtonText,
                ]}
              >
                🔍 Todos ({responses.length})
              </Text>
            </TouchableOpacity>

            {cloudNewerCount > 0 && (
              <TouchableOpacity
                style={[
                  styles.filterButton,
                  styles.cloudNewerFilterButton,
                  activeFilter === 'cloud-newer' && styles.activeCloudNewerFilterButton,
                ]}
                onPress={() => setActiveFilter('cloud-newer')}
              >
                <Text
                  style={[
                    styles.filterButtonText,
                    styles.cloudNewerFilterButtonText,
                    activeFilter === 'cloud-newer' && styles.activeFilterButtonText,
                  ]}
                >
                  📥 Desatualizados ({cloudNewerCount})
                </Text>
              </TouchableOpacity>
            )}

            {localNewerCount > 0 && (
              <TouchableOpacity
                style={[
                  styles.filterButton,
                  styles.localNewerFilterButton,
                  activeFilter === 'local-newer' && styles.activeLocalNewerFilterButton,
                ]}
                onPress={() => setActiveFilter('local-newer')}
              >
                <Text
                  style={[
                    styles.filterButtonText,
                    styles.localNewerFilterButtonText,
                    activeFilter === 'local-newer' && styles.activeFilterButtonText,
                  ]}
                >
                  📤 Atualizar Nuvem ({localNewerCount})
                </Text>
              </TouchableOpacity>
            )}

            {localOnlyCount > 0 && (
              <TouchableOpacity
                style={[
                  styles.filterButton,
                  styles.localOnlyFilterButton,
                  activeFilter === 'local-only' && styles.activeLocalOnlyFilterButton,
                ]}
                onPress={() => setActiveFilter('local-only')}
              >
                <Text
                  style={[
                    styles.filterButtonText,
                    styles.localOnlyFilterButtonText,
                    activeFilter === 'local-only' && styles.activeFilterButtonText,
                  ]}
                >
                  💾 Somente Local ({localOnlyCount})
                </Text>
              </TouchableOpacity>
            )}

            {syncedCount > 0 && (
              <TouchableOpacity
                style={[
                  styles.filterButton,
                  styles.syncedFilterButton,
                  activeFilter === 'synced' && styles.activeSyncedFilterButton,
                ]}
                onPress={() => setActiveFilter('synced')}
              >
                <Text
                  style={[
                    styles.filterButtonText,
                    styles.syncedFilterButtonText,
                    activeFilter === 'synced' && styles.activeFilterButtonText,
                  ]}
                >
                  ✅ Sincronizados ({syncedCount})
                </Text>
              </TouchableOpacity>
            )}
          </ScrollView>
        </View>
      )}

      {/* Resumo de estatísticas */}
      {responses.length > 0 && (
        <View style={styles.statsContainer}>
          <Card style={styles.statsCard}>
            <Card.Content>
              <View style={styles.statsRow}>
                {pendingCount > 0 && (
                  <>
                    <View style={styles.stat}>
                      <View style={styles.statHeader}>
                        <Text
                          style={[
                            styles.statNumber,
                            { color: pendingCount > 0 ? '#FF6F00' : '#AAAAAA' },
                          ]}
                        >
                          {pendingCount}
                        </Text>
                      </View>
                      <Text style={styles.statLabel}>Pendentes</Text>
                      <View style={styles.pendingChipContainer}>
                        <Text style={styles.pendingChipText}>⚠️ Ação Necessária</Text>
                      </View>

                      {/* Detalhes dos pendentes */}
                      {cloudNewerCount > 0 && (
                        <Text style={styles.statDetailText}>
                          📥 {cloudNewerCount} desatualizado{cloudNewerCount !== 1 ? 's' : ''}
                        </Text>
                      )}
                      {localNewerCount > 0 && (
                        <Text style={styles.statDetailText}>
                          📤 {localNewerCount} para atualizar
                        </Text>
                      )}
                      {localOnlyCount > 0 && (
                        <Text style={styles.statDetailText}>💾 {localOnlyCount} apenas local</Text>
                      )}
                    </View>
                    <Divider style={styles.statsDivider} />
                  </>
                )}

                <View style={styles.stat}>
                  <View style={styles.statHeader}>
                    <Text
                      style={[
                        styles.statNumber,
                        { color: syncedCount > 0 ? '#2E7D32' : '#AAAAAA' },
                      ]}
                    >
                      {syncedCount}
                    </Text>
                  </View>
                  <Text style={styles.statLabel}>Sincronizados</Text>
                  <View style={styles.syncedChipContainer}>
                    <Text style={styles.syncedChipText}>✅ Na Nuvem</Text>
                  </View>
                </View>
              </View>
            </Card.Content>
          </Card>
        </View>
      )}

      <ScrollView style={styles.content}>
        {responses.length === 0 ? (
          <Card style={styles.emptyCard}>
            <Card.Content>
              <View style={styles.emptyContent}>
                <Text style={styles.emptyIcon}>📝</Text>
                <Title style={styles.emptyTitle}>Nenhuma resposta ainda</Title>
                <Paragraph style={styles.emptyText}>
                  Você ainda não respondeu nenhum formulário.{'\n'}
                  Volte à tela inicial e comece a responder!
                </Paragraph>
              </View>
            </Card.Content>
            <Card.Actions style={styles.emptyActions}>
              <Button
                mode="contained"
                onPress={onBack}
                icon="clipboard-list"
                buttonColor={AVAQualisTheme.colors.primary}
                style={styles.emptyButton}
              >
                📋 Ir para Formulários
              </Button>
            </Card.Actions>
          </Card>
        ) : filteredResponses.length === 0 ? (
          <Card style={styles.noResultsCard}>
            <Card.Content>
              <View style={styles.noResultsContent}>
                <Text style={styles.noResultsIcon}>🔍</Text>
                <Title style={styles.noResultsTitle}>Nenhum resultado para este filtro</Title>
                <Paragraph style={styles.noResultsText}>
                  Não há formulários que correspondam ao filtro selecionado.{'\n'}
                  Tente selecionar outro filtro.
                </Paragraph>
              </View>
            </Card.Content>
            <Card.Actions style={styles.noResultsActions}>
              <Button
                mode="contained"
                onPress={() => setActiveFilter('all')}
                icon="filter-remove"
                buttonColor={AVAQualisTheme.colors.primary}
              >
                🔍 Ver Todos
              </Button>
            </Card.Actions>
          </Card>
        ) : (
          <>
            {/* Lista de Respostas Filtradas */}
            <View style={styles.responsesListContainer}>
              {filteredResponses.map(response => (
                <ResponseItem
                  key={response.id}
                  response={response}
                  formatDate={formatDate}
                  summary={responseSummaries[response.id] || '📊 Calculando estatísticas...'}
                  onSelect={handleSelectForm}
                  onDelete={deleteResponse}
                  onUpload={uploadResponse}
                  onDownloadCloud={downloadCloudVersion}
                  onViewCloudVersion={viewCloudVersion}
                  disabled={uploading}
                  uploading={uploading && currentUploadId === response.id}
                  serverStatus={serverStatus}
                  formTotalQuestions={formTotalQuestions}
                  responseValidations={responseValidations}
                />
              ))}
            </View>
          </>
        )}

        <View style={styles.bottomSpacer} />
      </ScrollView>

      {/* Botões flutuantes de ação em massa */}
      {pendingCount > 0 && (
        <View style={styles.fabContainer}>
          {/* Botão para baixar todas as versões desatualizadas */}
          {cloudNewerCount > 0 && (
            <Button
              mode="contained"
              onPress={downloadAllNewerVersions}
              loading={uploading}
              icon="cloud-download"
              buttonColor="#D32F2F"
              textColor={AVAQualisTheme.colors.onPrimary}
              style={[styles.fab, styles.downloadAllFab, { marginBottom: 10 }]}
              labelStyle={styles.fabLabel}
              disabled={serverStatus !== 'online'}
            >
              📥 Baixar Todos ({cloudNewerCount})
            </Button>
          )}

          {/* Botão para enviar todas as versões pendentes */}
          {(localNewerCount > 0 || localOnlyCount > 0) && (
            <Button
              mode="contained"
              onPress={uploadAllResponses}
              loading={uploading}
              icon="upload-multiple"
              buttonColor={localNewerCount > 0 ? '#FF6F00' : '#FF8F00'}
              textColor={AVAQualisTheme.colors.onPrimary}
              style={[styles.fab, localNewerCount > 0 ? styles.updateAllFab : styles.uploadAllFab]}
              labelStyle={styles.fabLabel}
              disabled={serverStatus !== 'online'}
            >
              📤 Enviar Todos ({localNewerCount + localOnlyCount})
            </Button>
          )}
        </View>
      )}

      {/* Loading overlay */}
      {uploading && (
        <View style={styles.uploadOverlay}>
          <View style={styles.uploadContent}>
            <ActivityIndicator size="large" color={AVAQualisTheme.colors.primary} />
            <Text style={styles.uploadText}>🔄 Sincronizando formulários...</Text>
            <Text style={styles.uploadSubtext}>⏳ Aguarde, não feche o aplicativo</Text>
          </View>
        </View>
      )}

      {/* Modal de Conflito */}
      <ConflictViewer
        visible={conflictModalVisible}
        onDismiss={() => setConflictModalVisible(false)}
        localVersion={localVersion}
        cloudVersion={cloudVersion}
        onUseLocal={resolveConflictWithLocal}
        onUseCloud={resolveConflictWithCloud}
      />

      {/* Custom Dialog Component */}
      <CustomDialog
        visible={dialogVisible}
        onDismiss={() => setDialogVisible(false)}
        title={dialogContent.title}
        message={dialogContent.message}
        actions={dialogContent.actions}
      />
    </View>
  );
}
